<!--
  SupplierSkuPanel — Section 1 of the hi-fi Product editor.

  Pebble 4 of "Product editor (hi-fi redesign)"
  https://trello.com/c/Qlwx8mlT

  Responsibilities:
    - Supplier select (SanMar default; others marked "Coming soon")
    - SKU autocomplete (server-driven via useSanmarCatalog)
    - Lookup refresh button (re-fires fetchMeta for the current SKU)
    - Meta grid: name / brand / base cost / sizes + fabric and description
    - Loading + error states for meta fetch
    - On mount: if serverState already has a product_item_number, fire
      fetchMeta so the grid populates without a user re-pick

  Only writes to the store via `updateField('product_item_number', sku)` —
  the top-level dot-free path that pebble 3 accepts. Nested paths (pebble
  12 territory) are NOT used here.
-->
<template>
  <v-card data-testid="supplier-sku-card">
    <v-card-title
      data-testid="supplier-sku-card-header"
      class="supplier-sku-header"
    >
      <v-icon icon="inventory_2" class="supplier-sku-header-icon" />
      <div class="supplier-sku-header-text">
        <div class="d-flex align-center">
            <div
              data-testid="supplier-sku-card-title"
              class="supplier-sku-title"
            >
              Supplier, SKU &amp; Inventory
            </div>
            <HelpIcon
                audience="owner"
                slug="set-up-a-product"
                heading="step-1-choose-the-garment"
            />
        </div>
        <div
          data-testid="supplier-sku-card-subtitle"
          class="supplier-sku-subtitle"
        >
          SKU pulls colors, sizes, and base pricing from the supplier
        </div>
      </div>
      <!--
        Names the SELECTED supplier, and appears only when that supplier
        actually auto-fills anything. It was the literal "Auto-filled from
        SanMar", which is a false claim on a non-SanMar product and a
        doubly false one where nothing is auto-filled at all.
      -->
      <div
        v-if="!supplierHasNoLiveInventory"
        data-testid="supplier-sku-card-eyebrow"
        class="supplier-sku-eyebrow"
      >
        Auto-filled from {{ supplierBadgeLabel }}
      </div>
    </v-card-title>

    <v-card-text>
      <div class="supplier-sku-body">
        <!--
          Preview image split (no card): the left column carries the
          existing supplier/SKU field row + meta grid (unchanged content),
          the right column shows a single derived preview image. The left
          column flexes to fill; the right column is a fixed comfortable
          width and wraps ABOVE the fields on narrow widths so the body
          never scrolls horizontally.
        -->
        <div class="supplier-sku-body-left">
      <div class="supplier-sku-field-row">
        <!--
          Bound to `supplierSelectValue`, never to `supplier` directly: a
          v-select prints a model value that matches no option VERBATIM, and
          `supplier` holds a KEY ('sanmar' before the vendor list lands).
          That is the lowercase "sanmar" Jake saw when the list failed.
        -->
        <v-select
          :model-value="supplierSelectValue"
          data-testid="supplier-sku-supplier-select"
          :items="supplierItems"
          :item-props="supplierItemProps"
          :menu-props="supplierMenuProps"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          class="supplier-sku-supplier-select"
          @update:model-value="onSupplierSelect"
        />

        <!--
          The cost of concealment, paid here.

          Excluded suppliers are now hidden rather than listed with a
          reason (Jake, reversing PR #3104: "We shouldn't leak to
          distributors that there are specific suppliers that aren't
          supported on their tier"). That reintroduces exactly the
          ambiguity the old label prevented -- a blank control explains
          nothing -- so an empty picker says something.

          The copy is deliberately incurious about WHY: it names no
          supplier, gives no count, and does not hint that a plan or tier
          gate exists. A "some suppliers are hidden" nudge would leak the
          very fact being concealed.

          Gated on `supplierListResolved` and not on an empty option list,
          because a FAILED fetch also leaves zero options -- and telling a
          user with a working account that no suppliers are set up for
          them, because of a transient network error, is a confidently
          wrong statement about their account. The message means "the
          server answered and offered nothing", never "we never heard
          back".
        -->
        <p
          v-if="supplierListResolved && supplierItems.length === 0"
          data-testid="supplier-sku-supplier-empty"
          class="supplier-sku-supplier-empty"
        >
          No suppliers are set up for your account yet &mdash; contact
          support to get started.
        </p>

        <!--
          The supplier's catalog is not loaded on this environment (S&S on a
          sandbox, which does not sweep S&S by default). Said plainly so an
          empty typeahead is not mistaken for "no such product".
        -->
        <v-alert
          v-if="catalog.error.value?.catalogNotLoaded"
          data-testid="supplier-sku-catalog-not-loaded"
          type="info"
          density="compact"
          class="supplier-sku-meta-alert"
        >
          {{ catalog.error.value.message }}
        </v-alert>

        <v-autocomplete
          data-testid="supplier-sku-typeahead"
          :model-value="selectedSkuDisplay"
          :items="catalogItemsForDisplay"
          :loading="catalog.loading.value"
          :error="!!catalog.error.value"
          :error-messages="catalogErrorMessages"
          item-title="displayLabel"
          item-value="pickKey"
          return-object
          no-filter
          density="compact"
          variant="outlined"
          hide-details
          class="supplier-sku-typeahead"
          @update:model-value="onSkuSelected"
          @update:search="onSearchInput"
        />

        <!--
          Automation sentinel: present only once the catalog has answered
          the query currently typed, with at least one row. A scenario waits
          on it before picking from the (testid-less) overlay; stale rows from
          an earlier query, an in-flight search, or an empty answer all keep
          it absent. Not visible.
        -->
        <span
          v-if="catalogResultsReady"
          data-testid="supplier-sku-typeahead-results-ready"
          hidden
        />

        <v-btn
          data-testid="supplier-sku-lookup-btn"
          :disabled="!localSku"
          icon="search"
          size="small"
          variant="text"
          aria-label="Re-fetch product details"
          class="supplier-sku-lookup-btn"
          @click="refetchMeta"
        />
      </div>

      <!--
        The vendor list did not arrive, after its retries. Said plainly,
        with a way out, instead of guessing a supplier: without the list we
        cannot tell which catalog this product belongs to, so the panel
        asks none (see onSupplierListFailed).
      -->
      <div
        v-if="supplierListFailed"
        data-testid="supplier-sku-supplier-load-error"
        class="supplier-sku-supplier-load-error"
        role="alert"
      >
        We couldn't load your suppliers, so this product's supplier details
        can't be shown right now.
        <button
          type="button"
          data-testid="supplier-sku-supplier-load-retry"
          class="supplier-sku-supplier-load-retry"
          @click="retrySupplierList"
        >
          Retry
        </button>
      </div>

      <div class="supplier-sku-meta-wrap">
        <v-progress-circular
          v-if="meta.loading.value"
          data-testid="supplier-sku-meta-loading"
          size="20"
          indeterminate
          class="supplier-sku-meta-loading"
        />
        <!--
          Automation sentinel: present once supplier meta has RESOLVED for
          the named SKU and nothing is loading. Everything the meta watcher
          stages for that payload (the confirmed-swap color reset, the
          name/description overwrite) is already in the dirty map by the
          time this renders, so a scenario can wait on it and then on the
          autosave-settled sentinel before reading the DB. Not visible.
        -->
        <span
          v-if="metaResolvedSku"
          :data-testid="`supplier-sku-meta-resolved-${metaResolvedSku}`"
          hidden
        />
        <!--
          S&S is rate limiting the shared account (HTTP 429
          supplier_throttled). Not a failure of the SKU: waiting fixes it, and
          "Look up" / re-picking the SKU retries. The same sentence is raised
          as a snackbar (see the watch on meta.error).
        -->
        <v-alert
          v-if="meta.error.value?.throttled"
          data-testid="supplier-sku-meta-throttled"
          type="info"
          density="compact"
          class="supplier-sku-meta-alert"
        >
          {{ throttleMessage(meta.error.value.retryAfterSeconds) }}
        </v-alert>
        <v-alert
          v-else-if="meta.error.value"
          data-testid="supplier-sku-meta-error"
          type="warning"
          density="compact"
          class="supplier-sku-meta-alert"
        >
          {{ meta.error.value.message }}
        </v-alert>

        <!--
          Bug Intake #451: the saved name is shared by several S&S styles and
          the product pins none, so the lookup refused to guess. Picking one
          pins supplier_style_id and re-runs the lookup for that style.
        -->
        <div
          v-if="meta.error.value?.ambiguousStyle"
          data-testid="supplier-sku-style-candidates"
          class="supplier-sku-style-candidates"
        >
          <v-select
            :model-value="null"
            data-testid="supplier-sku-style-picker"
            :items="styleCandidateItems"
            item-title="title"
            item-value="value"
            label="Which style do you mean?"
            density="compact"
            variant="outlined"
            hide-details
            class="supplier-sku-style-picker"
            @update:model-value="pickStyleCandidate"
          />
        </div>

        <!--
          Card #243 / GH #4443: Artifi cannot draw ANY color of this style --
          it does not know the SKU (404), or it knows the color names but none
          resolves to a renderable variant (PC380, ST350LS). That is a PREVIEW
          limit, not a setup error: the product sets up and sells normally, and
          the Stage offers the fallbacks. One plain notice for every supplier;
          the old "Double-check the SKU" copy sent people to re-check SKUs that
          were fine. Gated on !meta.error so a genuine meta-fetch failure keeps
          its own warning above and the two never stack.
        -->
        <div
          v-if="artifiStyleUnsupported && !meta.error.value"
          data-testid="supplier-sku-artifi-unsupported"
          class="supplier-sku-artifi-unsupported"
        >
          Automatic mockup previews aren't available for this style. You can
          still set up and sell it as usual. On the preview, add your own image
          for each color, or show the decoration on a zone diagram or an open
          canvas.
        </div>

        <div
          v-if="isFallback && hasAnyCpData"
          data-testid="supplier-sku-meta-fallback-notice"
          class="supplier-sku-meta-fallback-notice"
        >
          {{ fallbackNoticeText }}
        </div>

        <div
          data-testid="supplier-sku-meta-grid"
          class="supplier-sku-meta-grid"
          :class="{ 'is-loading': meta.loading.value, 'is-fallback': isFallback }"
        >
          <div class="supplier-sku-meta-cell">
            <div class="supplier-sku-meta-label">SKU</div>
            <div
              data-testid="supplier-sku-meta-sku"
              class="supplier-sku-meta-value"
            >
              {{ skuDisplay }}
            </div>
          </div>
          <div class="supplier-sku-meta-cell">
            <div class="supplier-sku-meta-label">Product name</div>
            <div
              data-testid="supplier-sku-meta-name"
              class="supplier-sku-meta-value"
            >
              {{ productNameDisplay }}
            </div>
          </div>
          <div class="supplier-sku-meta-cell">
            <div class="supplier-sku-meta-label">Brand</div>
            <div
              data-testid="supplier-sku-meta-brand"
              class="supplier-sku-meta-value"
            >
              {{ brandDisplay }}
            </div>
          </div>
          <div class="supplier-sku-meta-cell">
            <div class="supplier-sku-meta-label">Base cost</div>
            <div
              data-testid="supplier-sku-meta-base-cost"
              class="supplier-sku-meta-value"
            >
              {{ baseCostDisplay }}
            </div>
          </div>
          <div class="supplier-sku-meta-cell supplier-sku-meta-cell--half">
            <div class="supplier-sku-meta-label">Available sizes</div>
            <div
              data-testid="supplier-sku-meta-sizes"
              class="supplier-sku-meta-value"
            >
              {{ sizesDisplay }}
            </div>
          </div>

          <div class="supplier-sku-meta-cell supplier-sku-meta-cell--half">
            <div class="supplier-sku-meta-label">Fabric &amp; weight</div>
            <div
              data-testid="supplier-sku-meta-fabric"
              class="supplier-sku-meta-value"
            >
              {{ fabricDisplay }}
            </div>
          </div>
          <div class="supplier-sku-meta-cell supplier-sku-meta-cell--wide">
            <div class="supplier-sku-meta-label">Product description</div>
            <div
              data-testid="supplier-sku-meta-description"
              class="supplier-sku-meta-value"
            >
              {{ descriptionDisplay }}
            </div>
          </div>
        </div>
        </div>
        </div>

        <div
          data-testid="supplier-sku-preview"
          class="supplier-sku-preview"
        >
          <img
            v-if="previewImageUrl"
            data-testid="supplier-sku-preview-img"
            :src="previewImageUrl"
            :alt="previewImageAlt"
            class="supplier-sku-preview-img"
          />
          <div
            v-else
            data-testid="supplier-sku-preview-placeholder"
            class="supplier-sku-preview-placeholder"
            role="img"
            aria-label="No preview image available"
          >
            <v-icon
              icon="image_not_supported"
              class="supplier-sku-preview-placeholder-icon"
              aria-hidden="true"
            />
            <span class="supplier-sku-preview-placeholder-text">
              No preview image available
            </span>
          </div>
        </div>
      </div>
    </v-card-text>

    <!--
      Pebble 11 — destructive SKU swap confirmation. Only mounted; visibility
      is driven by `swapDialogOpen`. Reusable DestructiveConfirmDialog handles
      the dialog chrome.

      Copy fix (card "SKU-change -> update listing",
      https://trello.com/c/IGHhsZPQ): the prior copy claimed the swap would
      reset selected colors/decorations/sizes -- applySwap never touched any
      of those, so that was misleading. A confirmed swap overwrites the
      name/description (useSkuSwapCleanup, run by the editor shell)
      and, since 2026-10-07, deselects the selected colors the new style
      does not offer (the default color only when none survive) and removes
      every old-style image (useSkuSwapCleanup); since card #330 it also
      deselects the sizes the new style does not offer. Decorations are
      untouched, so the copy names exactly those consequences.
    -->
    <DestructiveConfirmDialog
      v-model="swapDialogOpen"
      title="Change supplier SKU?"
      description="Changing the SKU will replace this product's name and description with the new style's catalog values and remove all of its storefront images. Selected colors and sizes the new style doesn't offer will be removed. Your current name, description and images will be lost."
      confirm-label="Change SKU"
      :cancel-label="pendingSwap?.afterSupplierSwitch ? 'Pick a different style' : 'Keep current SKU'"
      :confirm-disabled="swapConfirmDisabled"
      @confirm="onSwapConfirm"
      @cancel="onSwapCancel"
    >
      <!--
        Card #545: a placement the NEW style does not carry gets a decision
        here, BEFORE the swap applies -- a valid location or remove it.
      -->
      <p
        v-if="swapViewsLoading"
        data-testid="sku-swap-placements-loading"
        class="text-caption"
      >
        Checking the new style's locations...
      </p>
      <SkuSwapPlacementDecisions
        v-model="swapDecisions"
        :stranded="swapStranded"
        :placements="store.decorations || []"
        :views="swapViews"
      />
    </DestructiveConfirmDialog>
  </v-card>

  <!--
    Live Inventory card (issue #5, wired 2026-06-17).

    Per the 2026-06-13 design bundle (IA-locked decision sec.7 + sec.9
    in "Locked Decisions.md"), the Supplier & SKU view carries a
    second card: live PromoStandards Inventory 2.0.0 readout
    showing on-hand by color x size.

    Data source: usePromostandardsInventoryStore (Pinia) -- backed by
    /api/supplier_integrations/promostandards/inventory with a 30 min
    Redis TTL cache. The Refresh button fires the DELETE variant which
    invalidates the cache and returns a fresh fetch.

    When the SKU is empty OR the supplier isn't supported (today:
    sanmar), the panel falls back to a deterministic
    sample table so the design IA is still reviewable on a fresh CP.
  -->
  <v-card
    data-testid="supplier-sku-live-inventory-card"
    class="live-inventory-card"
  >
    <v-card-title class="live-inventory-header">
      <v-icon icon="inventory" class="live-inventory-header-icon" />
      <div
        data-testid="live-inventory-card-title"
        class="live-inventory-title"
      >
        Live inventory
      </div>
      <!--
        The badge is a PROVENANCE CLAIM, so it renders only when there is a
        PromoStandards service behind the selected supplier. It used to be
        unconditional with a hardcoded 'SanMar' label, which announced
        PROMOSTANDARDS - SANMAR over a non-SanMar selection.
      -->
      <span
        v-if="!supplierHasNoLiveInventory"
        data-testid="live-inventory-badge"
        class="live-inventory-badge"
      >
        <span class="live-inventory-badge-dot" aria-hidden="true" />
        PromoStandards &middot; {{ supplierBadgeLabel }}
      </span>
      <span
        v-if="!supplierHasNoLiveInventory"
        data-testid="live-inventory-sync-label"
        class="live-inventory-sync"
      >
        <v-icon icon="schedule" size="14" />
        {{ lastQueriedLabel }}
      </span>
      <v-btn
        v-if="!supplierHasNoLiveInventory"
        data-testid="live-inventory-refresh-btn"
        class="live-inventory-refresh"
        size="small"
        variant="text"
        prepend-icon="refresh"
        :loading="liveInventory.loading"
        :disabled="liveInventory.loading || !canQueryLiveInventory || awaitingStyleAnswer"
        @click="refreshInventory"
      >
        Refresh
      </v-btn>
    </v-card-title>

    <v-card-text class="live-inventory-body">
      <!--
        THE SUPPLIER HAS NO LIVE-INVENTORY SERVICE.
        =========================================

        Chosen over HIDING the whole card. Jake's screenshot showed this
        card badged PROMOSTANDARDS - SANMAR with SanMar sample colors while
        S&S was selected, so the bug is the SanMar-branded sample table --
        and both hiding and this state remove it.

        Showing an honest empty state wins on two counts. A card that
        vanishes for some suppliers and not others reads as a broken page,
        and the distributor cannot tell "this supplier has no live
        inventory" from "the inventory card failed to load" -- which is the
        same class of ambiguity the empty supplier picker above already had
        to pay for with its own message. Second, the card is IA-locked in
        the 2026-06-13 design bundle (sec.7 + sec.9); removing it
        conditionally is a layout decision this fix has no mandate to make.

        So the card stays, states plainly that this supplier has no live
        inventory, and shows NO table -- no badge, no sync label, no
        refresh button, no fabricated rows.
      -->
      <div
        v-if="supplierHasNoLiveInventory"
        data-testid="live-inventory-no-integration"
        class="live-inventory-stub-notice"
      >
        {{ noLiveInventoryText }}
      </div>

      <div
        v-else-if="liveInventoryIsStub"
        data-testid="live-inventory-stub-notice"
        class="live-inventory-stub-notice"
      >
        {{ stubNoticeText }}
      </div>

      <div
        v-if="liveInventory.data && liveInventory.data.stale"
        data-testid="live-inventory-stale-banner"
        class="live-inventory-stale-banner"
      >
        Showing cached data -- the supplier inventory service is
        temporarily unavailable.
      </div>

      <div
        v-if="liveInventory.error"
        data-testid="live-inventory-error-banner"
        class="live-inventory-error-banner"
      >
        {{ liveInventory.error.message }}
        <button
          v-if="liveInventory.error.retryable !== false"
          type="button"
          data-testid="live-inventory-error-retry"
          class="live-inventory-error-retry"
          @click="refreshInventory"
        >
          Retry
        </button>
      </div>

      <!--
        The tables and their legend describe a supplier query. With no
        inventory service behind the selected supplier there was no query,
        so they render nothing rather than an empty scaffold implying one
        was made and came back bare.
      -->
      <section
        v-if="!supplierHasNoLiveInventory"
        data-testid="live-inv-in-use"
        class="live-inventory-section"
      >
        <h3
          data-testid="live-inv-in-use-heading"
          class="live-inventory-section-heading"
        >
          In use ({{ inUseRows.length }})
        </h3>
        <div v-if="inUseRows.length" class="live-inventory-table-wrap">
          <table
            data-testid="live-inventory-table-in-use"
            class="live-inventory-table"
          >
            <thead>
              <tr>
                <th class="live-inventory-col-color">Color</th>
                <th
                  v-for="sz in INVENTORY_SIZES"
                  :key="sz"
                  class="live-inventory-col-size"
                >
                  {{ sz }}
                </th>
                <th class="live-inventory-col-total">Total</th>
                <th class="live-inventory-col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in inUseRows"
                :key="row.sku"
                :data-testid="`live-inventory-row-${row.sku}`"
                :data-artifi-unsupported="
                  isRowUnsupported(row) ? 'true' : 'false'
                "
                :data-inventory-sample="row.is_sample ? 'true' : 'false'"
                :class="{
                  'is-artifi-unsupported': isRowUnsupported(row),
                  'is-inventory-sample': row.is_sample,
                }"
              >
                <td class="live-inventory-col-color">
                  <span class="live-inventory-color-cell">
                    <span
                      :data-testid="`live-inventory-swatch-${row.sku}`"
                      class="live-inventory-swatch"
                      :style="swatchStyle(row)"
                      aria-hidden="true"
                    />
                    <span class="live-inventory-color-name">{{ row.name }}</span>
                    <span
                      v-if="row.is_sample"
                      :data-testid="`live-inventory-sample-badge-${row.sku}`"
                      class="live-inventory-sample-badge"
                      >Sample</span
                    >
                    <span
                      v-if="isRowUnsupported(row)"
                      :data-testid="`live-inventory-unsupported-eye-${row.sku}`"
                      class="live-inventory-unsupported-eye material-icons"
                      title="No preview available for this color"
                      aria-hidden="true"
                      >visibility_off</span
                    >
                  </span>
                </td>
                <td
                  v-for="sz in INVENTORY_SIZES"
                  :key="sz"
                  :class="[
                    'live-inventory-col-size',
                    `live-inventory-cell-${classifyQty(row.bySize[sz])}`,
                  ]"
                >
                  <span
                    :class="[
                      'live-inventory-q',
                      `live-inventory-q-${classifyQty(row.bySize[sz])}`,
                    ]"
                  >
                    {{ formatQty(row.bySize[sz]) }}
                  </span>
                </td>
                <td class="live-inventory-col-total">
                  <span class="live-inventory-q live-inventory-q-total">
                    {{ row.total }}
                  </span>
                </td>
                <td class="live-inventory-col-status">
                  <span
                    :class="[
                      'live-inventory-status',
                      `live-inventory-status-${row.status}`,
                    ]"
                    :data-testid="`live-inventory-status-${row.sku}`"
                  >
                    {{ statusLabel(row.status) }}
                  </span>
                  <span
                    v-if="row.is_sample"
                    :data-testid="`live-inventory-sample-badge-${row.sku}`"
                    class="live-inventory-sample-badge"
                    title="Sample data -- not real supplier stock"
                  >
                    Sample
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p
          v-else
          data-testid="live-inv-in-use-empty"
          class="live-inventory-empty"
        >
          No colors selected for this product yet.
        </p>
      </section>

      <section
        v-if="!supplierHasNoLiveInventory"
        data-testid="live-inv-not-in-use"
        class="live-inventory-section"
      >
        <h3
          data-testid="live-inv-not-in-use-heading"
          class="live-inventory-section-heading"
        >
          Not in use ({{ notInUseRows.length }})
        </h3>
        <div v-if="notInUseRows.length" class="live-inventory-table-wrap">
          <table
            data-testid="live-inventory-table-not-in-use"
            class="live-inventory-table"
          >
            <thead>
              <tr>
                <th class="live-inventory-col-color">Color</th>
                <th
                  v-for="sz in INVENTORY_SIZES"
                  :key="sz"
                  class="live-inventory-col-size"
                >
                  {{ sz }}
                </th>
                <th class="live-inventory-col-total">Total</th>
                <th class="live-inventory-col-status">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in notInUseRows"
                :key="row.sku"
                :data-testid="`live-inventory-row-${row.sku}`"
                :data-artifi-unsupported="
                  isRowUnsupported(row) ? 'true' : 'false'
                "
                :data-inventory-sample="row.is_sample ? 'true' : 'false'"
                :class="{
                  'is-artifi-unsupported': isRowUnsupported(row),
                  'is-inventory-sample': row.is_sample,
                }"
              >
                <td class="live-inventory-col-color">
                  <span class="live-inventory-color-cell">
                    <span
                      :data-testid="`live-inventory-swatch-${row.sku}`"
                      class="live-inventory-swatch"
                      :style="swatchStyle(row)"
                      aria-hidden="true"
                    />
                    <span class="live-inventory-color-name">{{ row.name }}</span>
                    <span
                      v-if="row.is_sample"
                      :data-testid="`live-inventory-sample-badge-${row.sku}`"
                      class="live-inventory-sample-badge"
                      >Sample</span
                    >
                    <span
                      v-if="isRowUnsupported(row)"
                      :data-testid="`live-inventory-unsupported-eye-${row.sku}`"
                      class="live-inventory-unsupported-eye material-icons"
                      title="No preview available for this color"
                      aria-hidden="true"
                      >visibility_off</span
                    >
                  </span>
                </td>
                <td
                  v-for="sz in INVENTORY_SIZES"
                  :key="sz"
                  :class="[
                    'live-inventory-col-size',
                    `live-inventory-cell-${classifyQty(row.bySize[sz])}`,
                  ]"
                >
                  <span
                    :class="[
                      'live-inventory-q',
                      `live-inventory-q-${classifyQty(row.bySize[sz])}`,
                    ]"
                  >
                    {{ formatQty(row.bySize[sz]) }}
                  </span>
                </td>
                <td class="live-inventory-col-total">
                  <span class="live-inventory-q live-inventory-q-total">
                    {{ row.total }}
                  </span>
                </td>
                <td class="live-inventory-col-status">
                  <span
                    :class="[
                      'live-inventory-status',
                      `live-inventory-status-${row.status}`,
                    ]"
                    :data-testid="`live-inventory-status-${row.sku}`"
                  >
                    {{ statusLabel(row.status) }}
                  </span>
                  <span
                    v-if="row.is_sample"
                    :data-testid="`live-inventory-sample-badge-${row.sku}`"
                    class="live-inventory-sample-badge"
                    title="Sample data -- not real supplier stock"
                  >
                    Sample
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p
          v-else
          data-testid="live-inv-not-in-use-empty"
          class="live-inventory-empty"
        >
          <template v-if="liveInventoryIsEmptyResult">
            The supplier returned no inventory for this SKU in any colour.
          </template>
          <template v-else>
            No additional colors available from this supplier.
          </template>
        </p>
      </section>

      <div v-if="!supplierHasNoLiveInventory" class="live-inventory-legend">
        <span>
          <span class="live-inventory-q live-inventory-q-ok">50+</span>
          healthy
        </span>
        <span>
          <span class="live-inventory-q live-inventory-q-low">&lt;24</span>
          low
        </span>
        <span>
          <span class="live-inventory-q live-inventory-q-out">0</span>
          out of stock
        </span>
        <!--
          Names the selected supplier. Hardcoded "SanMar" here made the
          legend a live-query claim about a supplier that may not have
          been queried at all.
        -->
        <span class="live-inventory-legend-note">
          On-hand quantities only &middot; live query against the
          {{ supplierBadgeLabel }} PromoStandards Inventory service
        </span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useSupplierCatalog } from '@/composables/useSanmarCatalog'
import { useSanmarProductMeta } from '@/composables/useSanmarProductMeta'
import { throttleMessage } from '@/utils/supplierThrottle'
import { useProductEditorStore } from '@/stores/productEditor'
import { usePromostandardsInventoryStore } from '@/stores/promostandardsInventory'
import { formatCurrency } from '@/utils/currency'
import { deriveInventorySizes } from '@/utils/inventorySizes'
import { pickPreviewImage } from '@/utils/supplierPreviewImage'
import { isSanmarPlaceholderUrl } from '@/utils/sanmarPlaceholder'
import { useArtifiCoverage } from '@/composables/useArtifiCoverage'
import { useHideUnpreviewableColors } from '@/composables/useHideUnpreviewableColors'
import { colorNamesForSkus } from '@/utils/storefrontImages'
import { cleanCatalogName, decodeHtmlEntities } from '@/utils/catalogCopy'
import {
  clearListingImages,
  selectDefaultColor,
  snapshotSwapIdentity,
  snapshotSupplierSwitch,
} from '../useSkuSwapCleanup'
import {
  artifiPreviewEligible,
  hasCatalogIntegration,
  supplierDisplayName,
  supplierKeyForProduct,
} from '@/utils/supplier-entitlement'
import { useSupplierOptions } from '@/composables/useSupplierOptions'
import {
  sizesOffered,
  supplierSizeRun,
} from '@/pages/distributor/store-editor/StoreProducts/supplierSizes'
import DestructiveConfirmDialog from '../DestructiveConfirmDialog.vue'
import SkuSwapPlacementDecisions from '../SkuSwapPlacementDecisions.vue'
import { fetchArtifiProductViews } from '@/composables/useArtifiProductViews'
import api from '@/services/api'
import {
  decisionsComplete,
  strandedPlacements,
  swapRequestBody,
} from '@/utils/skuSwapPlacements'
import { NEW_PRODUCT_DEFAULT_SIZES } from '../sizeConstants'
import HelpIcon from '@/components/Help/HelpIcon.vue'

const store = useProductEditorStore()
// Vuex holds the shared notification toast (App.vue app-notification-toast).
const vuexStore = useStore()
// The typeahead searches the SELECTED supplier, not always SanMar (Jake,
// 8083, 2026-09-02: a non-integrated supplier's SKUs never appeared because
// every query went to SanMar's catalog).
//
// A GETTER, not the ref: `supplier` is declared further down this block, so
// passing it here would read it in its temporal dead zone. The getter is not
// called until the first search, by which time the ref exists.
const catalog = useSupplierCatalog(() => supplier.value)
// Constructed with GETTERS, not values: the picker can change between the
// mount-time pre-fill and a later "Look up" click, and a key captured here
// would send the second request to the first supplier. The composable
// re-reads both per call and declines to fetch at all when the selected
// supplier has no catalog integration. See its `fetchMeta` guard.
//
// Deliberately lazy (`() => ...`) rather than referencing `supplier` and
// `supplierItems` directly -- they are declared further down this file,
// and a getter defers the read to call time.
const meta = useSanmarProductMeta(
  () => supplier.value,
  () => supplierDisplayName(supplier.value, supplierItems.value),
)

// The Change SKU dialog's pre-swap lookup (card #545, gate P1-1): its OWN
// instance, so a lookup of a style that is never swapped to cannot reach this
// panel's grid or its empty-only auto-fill watchers (which would stage the
// new style's default color / name on a product that kept its SKU).
const swapLookup = useSanmarProductMeta(
  () => supplier.value,
  () => supplierDisplayName(supplier.value, supplierItems.value),
)

// The SKU whose meta is resolved and current -- drives the automation
// sentinel above. Null while a fetch is in flight.
const metaResolvedSku = computed(() =>
  meta.loading.value ? null : (meta.meta.value?.sku ?? null),
)

// A throttled S&S lookup gets a snackbar so a tester knows WHY nothing loaded
// (the account-wide budget is shared, so it can be somebody else's traffic).
// Fires per error OBJECT: every retry that is throttled again gets feedback.
// Any other failure (502, 404, a 401/403 credential outage) keeps its own
// message and never shows this one.
watch(
  () => meta.error.value,
  (err) => {
    if (err?.throttled) {
      vuexStore?.dispatch('raiseError', throttleMessage(err.retryAfterSeconds))
    }
  },
)

const inventoryStore = usePromostandardsInventoryStore()

// New-product default size set (Jake's call), shared with the swap clean-up
// in sizeConstants.js. Pre-selected when a NEW product's first SKU is picked
// so the distributor deselects the few they don't want rather than clicking
// each one on. It is a CANDIDATE set (Jake, 2026-10-07): only the sizes the
// supplier actually offers are selected -- see applyDefaultSizesFromMeta.

// Supplier select -- SERVER-SOURCED, and entitlement-aware.
//
// This list used to be a hardcoded three-item array (sanmar / ss / other,
// the latter two permanently "Coming soon"). That is why enabling a
// supplier in superadmin changed nothing here: no decision, from any
// backend, could reach a list that is a literal. Jake reported exactly
// that symptom against this picker.
//
// The rows now come from /vendor/getVendorList/, which stamps each vendor
// with `is_available` + `unavailable_reason` resolved against the
// EFFECTIVE distributor identity (so an SA impersonating a tenant is
// gated as that tenant). Nothing here decides entitlement -- this
// component renders a decision the server already made. A frontend filter
// is a hint; the gate that counts is the one in endpoints/vendor.py.
//
// An excluded supplier is HIDDEN -- no option, no label, nothing. This
// reverses PR #3104 at Jake's request: "We shouldn't leak to distributors
// that there are specific suppliers that aren't supported on their tier,
// at least for now." The load-bearing filter is the SERVER's (the row
// never reaches the browser for a scoped caller); `toSupplierOptions` is a
// second line for a payload that still carries a flagged row.
//
// What hiding costs is the ambiguity the label prevented, and that is paid
// by the empty-state message in the template above.
const SANMAR_SUPPLIER_KEY = 'sanmar'

// SUPPORTED_SUPPLIERS USED TO LIVE HERE, AND THAT WAS THE BUG.
//
// It was `['sanmar']`, and only TWO of the five supplier decisions in this
// file consulted it. The fallback notice and the live-inventory gate read
// it; the inventory badge hardcoded the string 'SanMar'; the Artifi gate
// used a NAME regex (`isSanmarSupplierName`); and the product-meta fetch
// never asked at all -- its endpoint carried a literal `sanmar`.
//
// So selecting S&S Activewear with SKU Q1000 rendered three contradictory
// things at once: an orange "Unknown SanMar SKU" error (wrong supplier), a
// blue "no catalog integration" notice (right supplier, contradicting the
// orange one), and a Live inventory card badged PROMOSTANDARDS - SANMAR
// showing SanMar sample colors.
//
// The predicate now lives in `@/utils/supplier-entitlement` and every site
// below reads it. It was also STALE: S&S has a real catalog searcher, a
// real product composer and real PromoStandards inventory server-side, so
// the list should never have been SanMar-only. See that module for the
// verification.
//
// `hasCatalogIntegration` FAILS CLOSED, which matters here specifically:
// `supplier` below is initialised to a pre-load default, so any surface
// evaluating before the vendor list lands sees a supplier that may not be
// the real one.

// Default while the vendor list is in flight. SanMar is the overwhelmingly
// common case and the only supplier with a live catalog today, so holding
// it keeps the SKU typeahead usable on first paint instead of blanking the
// panel for a round trip. `syncSupplierSelection` corrects it once the real
// list lands -- including clearing it if this distributor is NOT entitled
// to SanMar.
const supplier = ref(SANMAR_SUPPLIER_KEY)

// The editor's ONE vendor-list loader, provided by the ProductEditor shell
// (a fresh one when this panel is mounted on its own). It retries a
// transient failure and can be reloaded; see useSupplierOptions.
const supplierOptionsSource = useSupplierOptions()
const supplierItems = computed(() => supplierOptionsSource.options.value ?? [])

// What the picker DISPLAYS: the selection only when an option names it.
// Otherwise nothing -- never the raw key.
const supplierSelectValue = computed(() =>
  supplierItems.value.some((i) => i.value === supplier.value)
    ? supplier.value
    : null,
)

function onSupplierSelect(key) {
  supplier.value = key
  onSupplierPicked(key)
}

// Vuetify teleports the menu to <body>, outside this component, so the menu
// and its options carry their own testids for automation to read them
// (`dm-product-editor-supplier-picker-blank-goods-only`).
const supplierMenuProps = {
  contentProps: { 'data-testid': 'supplier-sku-supplier-menu' },
}

function supplierItemProps(item) {
  return {
    ...(item?.props ?? {}),
    'data-testid': `supplier-sku-supplier-option-${item?.value}`,
  }
}

// Did the server send us ANY vendor row, before entitlement filtering?
//
// This exists because filtering broke the signal `syncSupplierSelection`
// used to rely on. It read "no options" as "the server told us nothing",
// which was sound while excluded suppliers still arrived as disabled
// options. Now an all-excluded response and an empty response BOTH produce
// zero options, and those two need opposite handling: silence must leave
// the selection alone, denial must clear it.
//
// So the unfiltered response length is recorded separately. Null means no
// response has landed yet -- which is also how the empty-state message
// tells "the server offered nothing" apart from "the fetch failed".
const vendorRowsReceived = supplierOptionsSource.vendorRowCount

/** True once a vendor-list response has actually landed. */
const supplierListResolved = computed(() => vendorRowsReceived.value !== null)

// True once the list has LOADED -- the only state in which `supplier` names
// the product's real supplier rather than the pre-load default. Lookups
// (meta, live inventory) wait for it. A FAILED list never gets here: it used
// to leave the SanMar default standing "for good" and query SanMar with
// every product's SKU, which is a wrong-catalog request for anything not
// sourced from SanMar. Now a failure is said, and nothing is asked.
const supplierListLoaded = supplierOptionsSource.loaded
const supplierListFailed = supplierOptionsSource.failed

/**
 * Keep the selection on something the distributor may actually use.
 *
 * Left unguarded, the SanMar default above would survive a payload that
 * marks SanMar unavailable, and the gate would be visible but not
 * binding -- the panel would drive catalog and inventory lookups against
 * a supplier this distributor cannot sell from. Clearing to `null` is
 * deliberate over silently hopping to another supplier: a supplier is a
 * commercial choice, and picking one on the user's behalf is how a
 * product ends up sourced from a vendor nobody chose.
 */
function syncSupplierSelection() {
  // An EMPTY RESPONSE is not a denial. It means the server told us nothing
  // -- an un-provisioned tenant, a vendor table that has not been seeded,
  // a filter that matched none. Clearing the selection on silence would
  // break the SKU and live-inventory paths for a reason that is not an
  // entitlement decision, and would look identical to being gated.
  //
  // Note this tests the UNFILTERED count deliberately. Testing
  // `supplierItems` here would be the bug that hiding introduces: a
  // response of nothing-but-excluded-suppliers is an explicit DENIAL, and
  // it now yields zero options, so the two states would be conflated in
  // the direction that keeps driving catalog lookups against a supplier
  // the plan excludes.
  if (!vendorRowsReceived.value) return

  // THE PRODUCT'S OWN SUPPLIER WINS, AND NOTHING USED TO READ IT.
  //
  // `supplier` is initialised to a pre-load 'sanmar' default and this
  // function only ever asked "is the CURRENT selection still offered". For
  // a saved non-SanMar product the answer was yes -- SanMar is offered --
  // so the default survived and the picker showed SanMar for a product
  // sourced from someone else. Every downstream surface then followed it:
  // the meta fetch asked SanMar, 404'd, and rendered "Unknown SanMar SKU"
  // above a notice about the real supplier.
  //
  // A CampaignProduct persists its supplier as `supplier_id` (FK to
  // `vendors`) plus `supplier_name`, a denormalized copy of the vendor's
  // name. `supplierKeyForProduct` resolves the FK against the SERVER's own
  // options first and falls back to an exact name match, converting either
  // to a key, so nothing downstream ever sees the name.
  //
  // It reads the EFFECTIVE attributes, so a supplier switch staged by
  // `applySwap` survives a remount (ContextPanel v-if-mounts this panel per
  // tab) instead of snapping back to the saved supplier mid-edit.
  const savedId = store.getEffectiveAttribute?.('supplier_id')
  const savedName = store.getEffectiveAttribute?.('supplier_name')
  if (savedId != null || (savedName ?? '').toString().trim()) {
    const savedKey = supplierKeyForProduct(
      savedId,
      savedName,
      supplierItems.value,
    )
    if (savedKey) {
      supplier.value = savedKey
      return
    }
    // The saved supplier is not on offer -- excluded by the plan, renamed,
    // or deleted. Fall through to the fallback below rather than leaving
    // the pre-load default standing: a supplier we cannot offer must not
    // silently resolve to a DIFFERENT one that we can, without the
    // selection visibly moving.
  }

  const current = supplierItems.value.find((i) => i.value === supplier.value)
  if (current) return

  // Either the current selection is excluded (and so absent from the
  // options), or the server's list never contained it. Prefer SanMar when
  // it is offered; otherwise take the first offered row so the panel lands
  // on something usable rather than blank.
  const sanmar = supplierItems.value.find(
    (i) => i.value === SANMAR_SUPPLIER_KEY,
  )
  const fallback = sanmar ?? supplierItems.value[0]

  // No option at all -- every supplier this distributor might have used is
  // excluded. Clear, so the panel cannot drive lookups against a catalog
  // they may not sell from. The empty-state message explains the blank
  // without naming what is hidden.
  supplier.value = fallback ? fallback.value : null
}

/**
 * The vendor list landed (on mount, after a retry, or via Retry): put the
 * selection on the product's own supplier, then look the product up in it.
 */
function onSupplierListLoaded() {
  syncSupplierSelection()
  const sku = store.getEffectiveAttribute('product_item_number')
  if (sku) fetchMetaFor(sku, currentStyleId.value)
}

/**
 * The vendor list did not arrive, after its retries. We cannot tell which
 * supplier this product belongs to, so the selection is cleared rather than
 * left on the pre-load SanMar default: every surface reading `supplier` then
 * asks nothing, and the error above says why.
 */
function onSupplierListFailed() {
  supplier.value = null
}

function retrySupplierList() {
  supplierOptionsSource.reload()
}

watch(supplierListLoaded, (loaded) => {
  if (loaded) onSupplierListLoaded()
})
watch(supplierListFailed, (failed) => {
  if (failed) onSupplierListFailed()
})

// Local UI state for the autocomplete — the canonical value lives in the
// store under `product_item_number`. We hold a small object here so the
// widget can show the friendly "name" while we route the id to the store
// and to the meta fetch.
const selectedSku = ref(null)

// Build the user-facing label for an autocomplete row. Format:
//   `[SKU-ID] - [SKU-Title]`
// where the title has any trailing SKU stripped (SanMar's `name` field
// includes the SKU at the end, e.g. "Port & Co Core Fleece... PC78H"
// → we strip the trailing " PC78H" / ". PC78H" before displaying so
// the ID doesn't render twice). When the title is empty or absent
// (placeholder rows seeded on mount before SanMar resolves), fall back
// to the ID alone.
function buildSkuDisplayLabel(item) {
  if (!item || !item.id) return ''
  const id = String(item.id)
  const rawName = item.name ?? ''
  if (!rawName) return id
  const titleOnly = cleanCatalogName(rawName, id)
  if (!titleOnly) return id
  return `${id} - ${titleOnly}`
}

// See the results-ready sentinel in the template. `settledQuery` is read
// defensively: a catalog without it never reports ready, which only makes a
// waiting scenario time out rather than pick from stale rows.
const catalogResultsReady = computed(() =>
  !catalog.loading.value
  && catalog.results.value.length > 0
  && Boolean(catalog.query.value)
  && catalog.settledQuery?.value === catalog.query.value,
)

// The autocomplete's item value. `id` alone is NOT unique for S&S: one
// style NAME ("5200") is several styles across brands, each its own row
// with its own `style_id`, and rows sharing a value would select together.
function catalogPickKey(item) {
  return `${item?.id ?? ''}::${item?.style_id ?? ''}`
}

/**
 * Options for a meta fetch: the persisted style id when there is one.
 * Returns undefined for an unpinned product so its calls stay exactly
 * `fetchMeta(sku)`, as before.
 */
function metaOptions(styleId, extra = {}) {
  const opts = { ...extra }
  if (styleId != null) opts.styleId = styleId
  return Object.keys(opts).length ? opts : undefined
}

function fetchMetaFor(sku, styleId, extra = {}) {
  const opts = metaOptions(styleId, extra)
  if (opts) meta.fetchMeta(sku, opts)
  else meta.fetchMeta(sku)
}

// Catalog rows annotated with the formatted display label so the
// autocomplete's `item-title="displayLabel"` resolves correctly.
const catalogItemsForDisplay = computed(() =>
  catalog.results.value.map((row) => ({
    ...row,
    displayLabel: buildSkuDisplayLabel(row),
    pickKey: catalogPickKey(row),
  })),
)

// The selected-row needs the same `displayLabel` field so the
// autocomplete renders the SAME formatted string when collapsed.
// The banner used to say "Pick a SanMar SKU above to refresh from the live
// SanMar catalog" unconditionally. That was true when SanMar was the only
// supplier and is wrong now in two different ways: it names SanMar to someone
// who picked S&S, and it promises a "live catalog" to someone on a CUSTOM
// supplier, where no integration exists and no amount of picking will refresh
// anything. Jake, 2026-09-06: "too specific now that we have SanMar, S&S, and
// custom non-integrated suppliers."
//
// SUPPORTED_SUPPLIERS is the honest test: it is the set the SKU lookup is
// actually wired for, so it distinguishes "pick a SKU and we will fetch" from
// "these are the values you saved, and that is all there is".
const fallbackNoticeText = computed(() => {
  // No vendor list, so no known supplier: neither sentence below is true.
  if (supplierListFailed.value) {
    return 'Showing saved product values. Supplier details will load once your suppliers do.'
  }
  const key = supplier.value
  const label = supplierDisplayName(key, supplierItems.value)
  if (!hasCatalogIntegration(key)) {
    return `Showing saved product values. ${label} has no catalog integration, so these are the values entered for this product.`
  }
  return `Showing saved product values. Pick a ${label} SKU above to refresh from the live ${label} catalog.`
})

const selectedSkuDisplay = computed(() => {
  if (!selectedSku.value) return null
  return {
    ...selectedSku.value,
    displayLabel: buildSkuDisplayLabel(selectedSku.value),
    // The style comes from the ONE source every lookup reads (the staged
    // or saved supplier_style_id), never from a copy on selectedSku.
    pickKey: catalogPickKey({ id: selectedSku.value.id, style_id: currentStyleId.value }),
  }
})

// `localSku` reflects ONLY the user's current autocomplete selection. The
// lookup button binds to this so clearing the field disables the button —
// it must not silently fall back to the server-state SKU (gate finding
// P2-1: stale-state foot-gun, especially with pebble 11's swap modal).
const localSku = computed(() => selectedSku.value?.id ?? null)

// Vuetify v-autocomplete expects error-messages as an array (or string).
// Coerce the composable's `{ message }` shape into the array form so the
// catalog error surfaces under the field (gate finding P2-2).
const catalogErrorMessages = computed(() => {
  const err = catalog.error.value
  if (!err) return []
  return [err.message || 'Catalog search failed']
})

// When the SanMar lookup fails (404 or unset SKU) we fall back to whatever
// the CampaignProduct record already has. Real-world: a SKU's been retired
// from the feed but the saved CP still has cost/description/sizes locally.
// Interim world: the interim seeder uses placeholder SKUs that aren't in any feed.
// Either way, surfacing the saved values is more useful than a wall of "—".
const cpAttrs = computed(() => store.serverState?.data?.attributes ?? {})

const isFallback = computed(() => !meta.meta.value)

const productNameDisplay = computed(() => {
  // SanMar catalog names carry the SKU inline (e.g. "Port & Co Core Cotton
  // Tee. PC54"). Now that the SKU has its own meta cell, strip the redundant
  // trailing SKU so the name reads cleanly -- same cleanCatalogName helper the
  // typeahead label already uses (buildSkuDisplayLabel).
  const rawName = meta.meta.value?.name ?? cpAttrs.value.product_name
  if (rawName == null || rawName === '') return '—'
  const skuId = meta.meta.value?.sku ?? cpAttrs.value.product_item_number
  const cleaned = cleanCatalogName(rawName, skuId)
  return cleaned || '—'
})

const brandDisplay = computed(
  () => meta.meta.value?.brand ?? cpAttrs.value.supplier_name ?? '—',
)

// The SKU (SanMar style/item number). Prefer the live-from-feed meta value;
// fall back to the saved CampaignProduct.product_item_number (the persisted
// SKU) when the lookup is in fallback mode. Same idiom as the sibling
// display computeds above.
const skuDisplay = computed(
  () => meta.meta.value?.sku ?? cpAttrs.value.product_item_number ?? '—',
)

const baseCostDisplay = computed(() => {
  const cost = meta.meta.value?.base_cost ?? cpAttrs.value.supplier_cost
  // Canonical formatter — returns "$12.34" or "—" for null/NaN.
  return formatCurrency(cost)
})

const sizesDisplay = computed(() => {
  const sanmarSizes = meta.meta.value?.sizes
  // A lone one-size token (e.g. OSFA) reads as "One size" to match the
  // Sizes card's one-size state (issue #2203).
  if (sanmarSizes && sanmarSizes.length === 1) return 'One size'
  if (sanmarSizes && sanmarSizes.length > 0) return sanmarSizes.join(' · ')
  const selectedSizes = cpAttrs.value.selected_sizes
  if (selectedSizes && selectedSizes.length === 1) return 'One size'
  if (selectedSizes && selectedSizes.length > 0) return selectedSizes.join(' · ')
  return '—'
})

const fabricDisplay = computed(
  () => meta.meta.value?.fabric_weight ?? '—',
)

const descriptionDisplay = computed(() => {
  // SanMar catalog copy arrives with HTML entities (e.g. PC54's "&mdash;").
  // The live-from-feed `meta.description` is raw, so decode it exactly once
  // before display -- the same treatment the save path already applies (see
  // decodeHtmlEntities usage below). The saved cp fallbacks were decoded at
  // save time, which is why the Product Listing description already renders
  // correctly; this brings the read-only supplier meta into parity.
  const raw =
    meta.meta.value?.description
    ?? cpAttrs.value.long_description
    ?? cpAttrs.value.short_description
  if (raw == null || raw === '') return '—'
  return decodeHtmlEntities(raw)
})

// Show the fallback notice only when we're in fallback mode AND the CP
// actually has at least one usable saved value. Otherwise the "Unknown
// SKU" alert above is doing all the user-facing work.
const hasAnyCpData = computed(() => {
  const a = cpAttrs.value
  return Boolean(
    a.product_name
    || a.supplier_name
    || a.supplier_cost != null
    || (a.selected_sizes && a.selected_sizes.length > 0)
    || a.long_description
    || a.short_description,
  )
})

// Preview image (no-card UI polish). Derive a single "hero" preview from
// the chosen SKU's supplierMeta.image_urls_by_color -- the same store
// access StorefrontListingPanel uses. The selection heuristic
// (neutral-color + sans-model-front preference) lives in the pure,
// unit-tested pickPreviewImage helper; here we only feed it the map and
// defensively drop SanMar's "no image" placeholder so it never wins.
// Null -> the template renders the neutral placeholder tile.
const previewImageUrl = computed(() => {
  const byColor = store.supplierMeta?.image_urls_by_color
  const picked = pickPreviewImage(byColor)
  if (!picked || isSanmarPlaceholderUrl(picked)) return null
  return picked
})

// Alt text for the preview image: name the product when known so the
// preview is meaningful to screen-reader users, falling back to a generic
// label. Never surface the raw SKU-embedded catalog name (productNameDisplay
// already resolves the cleaned/live name or an em-dash).
const previewImageAlt = computed(() => {
  const name = productNameDisplay.value
  if (name && name !== '—') return `Product preview: ${name}`
  return 'Product preview'
})

// Pebble 11 — destructive SKU swap confirmation state.
//   - `swapDialogOpen` drives the DestructiveConfirmDialog modelValue.
//   - `pendingSwap` holds the user's pick while we wait for confirm/cancel.
//
// Dialog copy fix (card "SKU-change -> update listing",
// https://trello.com/c/IGHhsZPQ): the dialog used to claim the swap would
// "reset" selected colors / decorations / sizes -- applySwap never touched
// any of those, so the copy was flatly wrong. A confirmed swap loses the
// name/description (overwritten below), the selected colors the new style
// does not offer, and every old-style image (see useSkuSwapCleanup);
// the copy says exactly that.
const swapDialogOpen = ref(false)
const pendingSwap = ref(null) // { selected } when the user is mid-swap

// Card #545 (Jake, 2026-10-28): the NEW style's views decide which saved
// placements the swap would strand. Those need a decision (a valid location
// or remove) before Confirm, and the swap then goes through ONE server call
// (POST /campaign_products/<id>/swap_sku/) that applies the SKU and every
// decision together, so autosave never stores a broken placement. With no
// stranded placement -- or the new style's views unknown (fail open) -- the
// swap stays on the autosave path; the server's hide rule is the safety net.
const swapViews = ref([])
const swapViewsLoading = ref(false)
const swapDecisions = ref({})
const swapApplying = ref(false)
let swapViewsToken = 0
const swapStranded = computed(() =>
  strandedPlacements(store.decorations || [], swapViews.value),
)
const swapConfirmDisabled = computed(
  () =>
    swapViewsLoading.value
    || swapApplying.value
    || !decisionsComplete(swapStranded.value, swapDecisions.value),
)

async function loadSwapViews(sku) {
  swapViewsToken += 1
  const token = swapViewsToken
  swapViews.value = []
  swapDecisions.value = {}
  swapViewsLoading.value = true
  const { views } = await fetchArtifiProductViews(sku)
  if (token !== swapViewsToken) return
  swapViews.value = Array.isArray(views) ? views : []
  swapViewsLoading.value = false
}

function resetSwapDecisions() {
  swapViewsToken += 1
  swapViews.value = []
  swapViewsLoading.value = false
  swapDecisions.value = {}
}

const SWAP_PRECHECK_FAILED_MESSAGE
  = "The new item's catalog could not be loaded. Nothing was changed; "
  + 'pick the item again to retry.'

/**
 * Look the new style up before the server swap. True when the lookup did
 * not fail for this SKU (a supplier with no catalog resolves quietly, with
 * no error: nothing to check, and nothing the clean-up will need).
 */
async function lookUpNewStyle(selected) {
  const styleId = selected.style_id ?? null
  const opts = metaOptions(styleId)
  if (opts) await swapLookup.fetchMeta(selected.id, opts)
  else await swapLookup.fetchMeta(selected.id)
  const err = swapLookup.error.value
  return !(err && (!err.sku || err.sku === selected.id))
}

function swapErrorMessage(err) {
  const body = err?.response?.data
  if (typeof body?.error === 'string' && body.error) return body.error
  return "The SKU couldn't be changed. Nothing was saved; try again."
}

function onSkuSelected(selected) {
  // Clearing the autocomplete (null) — track local state only, no write.
  if (!selected || !selected.id) {
    selectedSku.value = selected
    return
  }

  // The supplier the SKU was picked under, captured NOW: the picker can
  // move while the confirm dialog is open, and the SKU belongs to the
  // catalog it was searched in, not to whatever is selected at confirm.
  const supplierOption = selectedSupplierOption()

  const saved = store.serverState?.data?.attributes ?? {}
  // A SKU cleared by a supplier switch (clearSkuForSupplierSwitch) is not a
  // SKU to swap away from: the pick is a first pick of the new supplier's
  // product, with nothing old left to confirm the loss of.
  const skuCleared = !(store.getEffectiveAttribute?.('product_item_number') ?? '')
    .toString()
    .trim()
  const currentSkuId = skuCleared ? null : saved.product_item_number
  // A different supplier is a different product even when the style number
  // matches -- numbers collide across catalogs ("39" is Gildan 2000 at S&S).
  // Compared as resolved KEYS, so a legacy row that carries only a
  // supplier_name is still recognized. Only a KNOWN saved supplier can
  // differ: a row with neither id nor name (the PC54 shape) re-picking its
  // SKU is not a swap.
  const savedKey = supplierKeyForProduct(
    saved.supplier_id,
    saved.supplier_name,
    supplierItems.value,
  )
  const savedSupplierKnown = saved.supplier_id != null
    || Boolean((saved.supplier_name ?? '').toString().trim())
  const supplierChanged = savedSupplierKnown
    && supplierOption != null
    && supplierOption.value !== savedKey
  // Same name, different STYLE: "5200" is C2 Sport, Gildan and Bayside at
  // S&S, so a different style id is a different product. Only a KNOWN
  // saved id can differ; an unpinned row re-picking its name pins it.
  const savedStyleId = saved.supplier_style_id ?? null
  const styleChanged = savedStyleId != null
    && selected.style_id != null
    && selected.style_id !== savedStyleId
  if (
    currentSkuId
    && (currentSkuId !== selected.id || supplierChanged || styleChanged)
  ) {
    // Destructive swap — gate behind the confirm modal. Do NOT mutate
    // selectedSku yet; on cancel the autocomplete display should not
    // reflect the unsaved pick. The modal will drive the apply.
    pendingSwap.value = { selected, supplierOption }
    swapDialogOpen.value = true
    loadSwapViews(selected.id)
    return
  }

  // The first pick after a supplier switch replaces the old item's content:
  // the switch already told the distributor the SKU was cleared, so there is
  // no second dialog, but the clean-up that overwrites the name, descriptions
  // and sizes with the new item's catalog values DOES run (card #349; the
  // plain empty-only autofill kept the old item's name and S-2XL sizes on a
  // One Size bag).
  //
  // EXCEPT when the product still holds placements (card #545 retest): the
  // new style may not carry one of their locations, and this pick would
  // otherwise store it stranded (PC78 -> S&S -> SanMar C402 left a Left chest
  // placement on a cap). Those get the same decide-first dialog as any swap.
  if (skuCleared && store.skuClearedForSwitch === true) {
    gateSupplierSwitchPick(selected, supplierOption)
    return
  }

  // First-time pick OR same-SKU re-pick — proceed immediately. Not
  // destructive: the empty-only auto-fill watcher below governs name/
  // description here, same as always.
  applySwap(selected, { destructive: false, supplierOption })
}

/**
 * The first style pick after a supplier switch (card #545 retest).
 *
 * The switch cleared the SKU, so there is no old SKU to confirm the loss of
 * and no dialog by default. A saved placement the NEW style does not carry
 * changes that: it is decided in the Change SKU dialog before anything is
 * applied, exactly as for a same-supplier swap. The dialog opens only for a
 * stranded placement, so the common pick stays a single step. With no
 * placements, or the new style's views unknown (fail open, the server's hide
 * rule is the net), the swap applies as before.
 */
async function gateSupplierSwitchPick(selected, supplierOption) {
  const apply = () =>
    applySwap(selected, { destructive: true, supplierOption })
  if (!(store.decorations || []).length) {
    apply()
    return
  }
  swapViewsToken += 1
  const token = swapViewsToken
  const { views } = await fetchArtifiProductViews(selected.id)
  // A later pick, cancel or supplier switch voids this one.
  if (token !== swapViewsToken) return
  const newViews = Array.isArray(views) ? views : []
  if (strandedPlacements(store.decorations || [], newViews).length === 0) {
    apply()
    return
  }
  swapViews.value = newViews
  swapDecisions.value = {}
  pendingSwap.value = { selected, supplierOption, afterSupplierSwitch: true }
  swapDialogOpen.value = true
}

/**
 * The user switched the supplier picker.
 *
 * A product with NO SKU yet takes the picked supplier as its own. The SKU
 * pick (applySwap) is what stages a supplier on a product that has one, but
 * with no SKU there is nothing to disagree with, and staging nothing lost
 * the choice: the save carried no supplier and the next load fell back to
 * SanMar. A supplier whose catalog cannot be searched (a keyless local
 * vendor) could then never be saved at all (Jake, 2026-10-08).
 *
 * A product that HAS a SKU loses it (Jake, 2026-10-14). A SKU belongs to
 * exactly one supplier's catalog, so leaving it behind pointed the product
 * at a part the new supplier does not make -- and staged nothing, so the
 * picker said one supplier while the saved product said another. The SKU
 * is cleared with what was chosen against it, the new supplier is staged in
 * its place, and a toast says so. The server enforces the same rule on the
 * model (campaign_product.py, _clear_sku_on_supplier_switch), so a client
 * that skips this still cannot persist the mismatch.
 *
 * Bound to the select's user event, not a watch on `supplier`, so the
 * reconcile in syncSupplierSelection -- a correction, not a choice -- never
 * stages anything.
 */
function onSupplierPicked(key) {
  const option = supplierItems.value.find((i) => i.value === key)
  if (option?.vendorId == null) return
  const sku = store.getEffectiveAttribute?.('product_item_number')
  if ((sku ?? '').toString().trim()) {
    // The supplier the picker showed before this change: the product's own,
    // or SanMar (the picker's default) for a row that names none.
    const currentKey = supplierKeyForProduct(
      store.getEffectiveAttribute?.('supplier_id'),
      store.getEffectiveAttribute?.('supplier_name'),
      supplierItems.value,
    ) ?? SANMAR_SUPPLIER_KEY
    if (currentKey === key) return
    clearSkuForSupplierSwitch(option)
  }
  store.updateField('supplier_id', option.vendorId)
  store.updateField('supplier_name', option.title)
}

/**
 * Drop the SKU and everything tied to it, and tell the distributor.
 *
 * The same things a CONFIRMED SKU swap drops (see useSkuSwapCleanup):
 * the selected colors, and every old-style image. Any swap still waiting on
 * a confirm or a catalog answer is void -- its SKU belongs to the supplier
 * just left.
 */
function clearSkuForSupplierSwitch(option) {
  // Remember what the product holds BEFORE it is cleared (card #542): the
  // swap that follows snapshots "what it points at NOW", which by then is the
  // blanked state, and a failed lookup rolled back to a product with no SKU
  // and no colors. The FIRST snapshot wins, so a second switch before a pick
  // still rolls back to the original.
  if (!store.supplierSwitchSnapshot) {
    store.setSupplierSwitchSnapshot?.(snapshotSupplierSwitch(store))
  }
  pendingSwap.value = null
  swapDialogOpen.value = false
  swapViewsToken += 1
  store.setPendingSwapCleanup?.(null)
  // The product still carries the OLD item's name, descriptions and sizes:
  // the next pick is a swap, not a first pick (see onSkuSelected).
  store.setSkuClearedForSwitch?.(true)
  pendingDefaultSizesSku.value = null
  selectedSku.value = null

  store.updateField('product_item_number', null)
  store.updateField('supplier_style_id', null)
  store.updateField('selected_color_skus', [])
  store.updateField('selected_colors', [])
  clearListingImages(store)
  vuexStore?.dispatch(
    'raiseInfo',
    `Supplier changed: SKU cleared. Pick a product from ${option.title}.`,
  )
}

/** The option row for the supplier currently selected in the picker. */
function selectedSupplierOption() {
  return supplierItems.value.find((i) => i.value === supplier.value) ?? null
}

// The color / image / copy clean-up a CONFIRMED destructive swap owes the
// product is armed in the editor store (`pendingSwapCleanup`) and run by the
// editor shell (useSkuSwapCleanup), NOT here: this panel is only mounted
// while its tab is active, and a clean-up held in a component ref was lost
// whenever the user left the tab before the new catalog loaded (card #349).
// The old style's color names are captured at arm time because only the OLD
// catalog can resolve the old skus; by the time the new meta resolves the
// shell has already republished store.supplierMeta for the new style.

// The SKU whose size run the new-product default sizes are waiting for
// (see applySwap / applyDefaultSizesFromMeta). Null when none is awaited.
const pendingDefaultSizesSku = ref(null)

function applySwap(
  selected,
  { destructive = false, supplierOption = null, committed = false } = {},
) {
  // What the product points at NOW, taken before anything is staged: a failed
  // catalog lookup puts it back (failSwapCleanup, card #349).
  // After a supplier switch that is the product as it was BEFORE the switch
  // cleared it (card #542), and the rollback leaves it an ordinary product.
  const switchSnapshot = store.supplierSwitchSnapshot ?? null
  const previous = switchSnapshot ?? snapshotSwapIdentity(store)
  const previousSkuClearedForSwitch = switchSnapshot
    ? false
    : store.skuClearedForSwitch === true
  selectedSku.value = selected
  store.updateField('product_item_number', selected.id)
  // The style the row stands for (S&S styleID), or null for a supplier
  // whose SKU names one style. Always staged with the SKU, so a pick never
  // leaves the previous style's id behind.
  const styleId = selected.style_id ?? null
  store.updateField('supplier_style_id', styleId)

  // THE SUPPLIER TRAVELS WITH THE SKU. A SKU only means something inside
  // the catalog it was picked from, so the pick stages that supplier too.
  // Staging only the SKU saved it against the OLD supplier: the editor then
  // asked SanMar about an S&S style (404) and the picker snapped back to
  // SanMar (dev.threadflow.com, 2026-10-05). Both fields are staged so the
  // FK and its denormalized name never disagree; the server derives the
  // name from the id anyway.
  //
  // On a product that already has a SKU, a supplier change with NO SKU pick
  // stages nothing -- `supplier` is a local ref until here -- so its supplier
  // never moves away from its SKU. A product with no SKU yet stages the
  // supplier on the pick itself (onSupplierPicked).
  if (supplierOption?.vendorId != null) {
    store.updateField('supplier_id', supplierOption.vendorId)
    store.updateField('supplier_name', supplierOption.title)
  }
  if (destructive) {
    store.setPendingSwapCleanup?.({
      sku: selected.id,
      oldNames: colorNamesForSkus(
        store.getEffectiveAttribute('selected_color_skus'),
        store.supplierMeta?.colors,
      ),
      // A swap the server already committed (card #545) is never rolled
      // back client-side: see SWAP_COMMITTED_LOOKUP_FAILED_MESSAGE.
      previous: committed ? null : previous,
      committed,
      previousSkuClearedForSwitch,
    })
    store.setSkuClearedForSwitch?.(false)
  } else {
    store.setPendingSwapCleanup?.(null)
  }
  // New-product convenience default (Jake's call): when the product has
  // NO sizes selected yet, pre-select the default size set (XS-XL + 2XL)
  // so the distributor deselects the few they don't want instead of
  // clicking each on. Gated on "no effective sizes" so an EXISTING product
  // (which already carries a saved selected_sizes) is never clobbered --
  // including on a destructive SKU swap, where the saved sizes the new style
  // offers stay (the rest are dropped by useSkuSwapCleanup). A new product where the user already toggled a
  // size manually is likewise left alone (effective set is non-empty).
  // 3XL/4XL/5XL stay opt-in and are deliberately excluded.
  //
  // The set is a CANDIDATE list (Jake, 2026-10-07): for a supplier with a
  // catalog it is applied by applyDefaultSizesFromMeta once THIS SKU's size
  // run has loaded, keeping only the sizes the supplier offers -- SanMar
  // PC54 starts at S, and writing XS here put an unbuyable size on three
  // production stores. If the run never loads (a lookup error, or the user
  // leaves the tab first), nothing is written: a size the supplier is not
  // known to offer is never selected. A supplier with no catalog has no run
  // to check, so it keeps the plain default, written at once.
  pendingDefaultSizesSku.value = null
  const currentSizes = store.getEffectiveAttribute('selected_sizes')
  if (!Array.isArray(currentSizes) || currentSizes.length === 0) {
    if (hasCatalogIntegration(supplier.value)) {
      pendingDefaultSizesSku.value = selected.id
    } else {
      store.updateField('selected_sizes', [...NEW_PRODUCT_DEFAULT_SIZES])
    }
  }
  fetchMetaFor(selected.id, styleId)
}

/**
 * Apply the new-product default sizes once the awaited SKU's size run has
 * loaded: the candidates the supplier offers, compared alias-aware (a
 * supplier XXL keeps the default 2XL). Consumed once; a payload for another
 * SKU is ignored, and a size the user picked meanwhile wins.
 */
function applyDefaultSizesFromMeta(newMeta) {
  const awaited = pendingDefaultSizesSku.value
  if (!awaited || newMeta.sku !== awaited) return
  pendingDefaultSizesSku.value = null

  const currentSizes = store.getEffectiveAttribute('selected_sizes')
  if (Array.isArray(currentSizes) && currentSizes.length > 0) return

  const offered = sizesOffered(NEW_PRODUCT_DEFAULT_SIZES, supplierSizeRun(newMeta))
  if (offered.length > 0) store.updateField('selected_sizes', offered)
}

async function onSwapConfirm() {
  const pending = pendingSwap.value
  if (!pending || swapConfirmDisabled.value) return
  const stranded = swapStranded.value
  if (stranded.length === 0) {
    applySwap(pending.selected, {
      destructive: true,
      supplierOption: pending.supplierOption,
    })
    pendingSwap.value = null
    resetSwapDecisions()
    return
  }
  // Stranded placements: the SKU and the decisions are written together by
  // the server, or not at all. Only on success is the rest of the swap
  // (copy, colors, images, sizes) staged, as for any confirmed swap.
  //
  // The new style's catalog is looked up FIRST (gate P1-1, PR #4918): a
  // committed server swap cannot be rolled back client-side, so a lookup
  // that fails must fail before anything is written.
  swapApplying.value = true
  if (!(await lookUpNewStyle(pending.selected))) {
    swapApplying.value = false
    pendingSwap.value = null
    resetSwapDecisions()
    syncSupplierSelection()
    vuexStore?.dispatch('raiseError', SWAP_PRECHECK_FAILED_MESSAGE)
    return
  }
  try {
    await api.post(
      `/campaign_products/${store.campaignProductId}/swap_sku/`,
      swapRequestBody({
        selected: pending.selected,
        supplierOption: pending.supplierOption,
        stranded,
        decisions: swapDecisions.value,
      }),
    )
  } catch (err) {
    swapApplying.value = false
    pendingSwap.value = null
    resetSwapDecisions()
    syncSupplierSelection()
    vuexStore?.dispatch('raiseError', swapErrorMessage(err))
    return
  }
  swapApplying.value = false
  pendingSwap.value = null
  resetSwapDecisions()
  applySwap(pending.selected, {
    destructive: true,
    supplierOption: pending.supplierOption,
    committed: true,
  })
  // The placements moved or went away on the server: reload them in the
  // background so the editor keeps its place.
  await store.loadServerState?.({ force: true, background: true })
}

function onSwapCancel() {
  // Drop the pending pick. The autocomplete's visual model is bound to
  // `selectedSku` which we deliberately did NOT mutate in the destructive
  // path, so the prior selection stays on screen. The autocomplete may
  // briefly show the unpicked value internally; restoring the display is
  // a P2 follow-up item (see pebble 11 task body).
  pendingSwap.value = null
  resetSwapDecisions()
  // The supplier picker may have been moved to the pick's supplier before
  // the dialog opened; nothing was staged, so snap it back to the product's
  // own so the dropdown and the SKU cannot disagree (card #350).
  syncSupplierSelection()
}

function onSearchInput(q) {
  // useSupplierCatalog owns debounce + min-length + abort — just forward.
  // It also re-reads the SELECTED supplier per dispatch, so a search fired
  // after the picker changes goes to the new supplier without rewiring here.
  catalog.search(q)
}

/** "Gildan - Unisex Heavy Cotton Tank Top" (brand first; either may be absent). */
function styleCandidateLabel(candidate) {
  const parts = [candidate?.brand, candidate?.title].filter(Boolean)
  return parts.length ? parts.join(' - ') : `Style ${candidate?.style_id}`
}

/** The ambiguous lookup's candidates, as v-select items. */
const styleCandidateItems = computed(() =>
  (meta.error.value?.candidates ?? []).map((candidate) => ({
    value: candidate.style_id,
    title: styleCandidateLabel(candidate),
  })),
)

/**
 * The distributor chose one of the styles an ambiguous name could mean:
 * pin it (so every later lookup keys on the id) and look that style up.
 */
function pickStyleCandidate(styleId) {
  const sku = localSku.value
  if (!sku || styleId == null) return
  store.updateField('supplier_style_id', styleId)
  fetchMetaFor(sku, styleId)
}

function refetchMeta() {
  const sku = localSku.value
  if (!sku) return
  // The manual "Look up" button forces a fresh compose (bypass the
  // backend catalog-detail cache) -- the user clicked it precisely
  // because they want current supplier data, not a warm-cache reuse.
  fetchMetaFor(sku, currentStyleId.value, { refresh: true })
}

// Note (2026-05-18 currency sweep): the previous local `formatUsd` was
// replaced by `formatCurrency` from `@/utils/currency` — see top-of-file
// import. The behavior is equivalent (handles null/NaN → "—", $-prefixed
// 2dp format) and now routes through the canonical implementation.

// Pebble 5 — Section 2 needs supplier meta to populate the storefront
// listing (default product_name, image lightbox sources). Publish to the
// store any time the composable resolves new meta, and auto-populate the
// storefront name + long description from the catalog when the editor
// hasn't set them yet.
//
// Auto-populate rules (Jake, 2026-07-08, revised same day after 8083 test):
//   - product_name: fill/replace when it is empty, null, OR a known
//     new-product placeholder, using the CLEANED catalog name (SKU
//     stripped, leading/trailing punctuation trimmed). Never clobber a
//     real user/server value. The stub-create endpoint seeds the name as
//     "New product" (lowercase "p", api/src/endpoints/campaign_product.py);
//     both casings are treated as placeholders so a first SKU pick fills
//     the name (fix 1).
//   - short_description: SanMar has no short_description field, so we
//     DERIVE it from the same cleaned SKU-name title used for
//     product_name. Filled only when empty; never overwrites a user value
//     (fix 2).
//   - long_description: SanMar's catalog exposes a SINGLE `description`
//     field (marketing bullet copy) -> map it to long_description, filled
//     only when empty.
//   - All filled text is HTML-entity-decoded first (SanMar copy contains
//     &mdash; &amp; &#39; etc. inconsistently) so the storefront renders
//     the real characters, not literal entity markup (fix 3).
const AUTO_FILL_NAME_PLACEHOLDERS = new Set(['New Product', 'New product'])

function isEmptyOrPlaceholderName(value) {
  return value == null || value === '' || AUTO_FILL_NAME_PLACEHOLDERS.has(value)
}

/**
 * Enable a default color for a product that has none yet.
 *
 * Called from the meta watcher below with a freshly-resolved SanMar payload.
 * Writes at most once per product: the gate is "no effective colors", read
 * through getEffectiveAttribute so an in-flight (unsaved) selection counts.
 * That single gate covers every case we must not clobber --
 *
 *   - an EXISTING product with saved colors (non-empty serverState),
 *   - a product whose colors the user just CLEARED in this session (the
 *     empty array is in the dirty map, and an empty effective list only
 *     reaches the write below when the user has not acted -- see the
 *     stale-pick guard, which is what stops an unrelated later resolution
 *     from firing), and
 *   - a second resolution for the same SKU (by then the color is dirty).
 *
 * The write is a SAVE, not a suggestion: `updateField` arms the editor's
 * 700ms autosave debounce, so the enabled color persists exactly like a
 * color the user clicked. A pre-selection that did NOT persist would be
 * worse -- the distributor would see an enabled swatch, navigate away, and
 * find the product still had no colors.
 */
// How long the default pick waits for Artifi coverage after the catalog
// resolves before it gives up and decides without it (fail-open).
const DEFAULT_COLOR_COVERAGE_WAIT_MS = 6000

// The catalog payload whose default pick is parked until coverage settles.
let pendingDefaultMeta = null
let pendingDefaultTimer = null

function clearPendingDefault() {
  pendingDefaultMeta = null
  if (pendingDefaultTimer !== null) {
    clearTimeout(pendingDefaultTimer)
    pendingDefaultTimer = null
  }
}

// This panel is mounted only while its tab is active, so leaving the tab while
// a pick waits for coverage must not CANCEL it (the product would be left with
// no color). Apply it now, without coverage (among colors not KNOWN
// unrenderable); every guard re-runs, so a stale SKU or a user action still
// wins. Cancellation on a SKU change is the stale-SKU guard, not this hook.
onBeforeUnmount(() => {
  const parked = pendingDefaultMeta
  clearPendingDefault()
  if (parked) applyDefaultColorFromMeta(parked, { coverageSettled: true })
})

function applyDefaultColorFromMeta(newMeta, { coverageSettled = false } = {}) {
  // Stale-resolution guard, mirroring the swap clean-up's once-only SKU match: a
  // payload that resolved for a SKU the user has since moved off must not
  // drive a write for the CURRENT product. `currentSku` reads the effective
  // value, so it already reflects the pick that is actually in force.
  if (!newMeta.sku || newMeta.sku !== currentSku.value) return

  // Any user action on colors in this session -- including CLEARING them --
  // lands `selected_color_skus` in the editor's dirty map. Its mere PRESENCE
  // there is the signal that the user has spoken, whatever the value, so it
  // is the gate that makes `colors-remove-all` stick. This is the only
  // available signal for that fact: the column is `nullable=False` with a
  // `'[]'` server default, so once saved, a cleared list and a never-set one
  // are byte-identical (api/src/models/campaign_product.py:164-179).
  if (store.dirty?.has?.('selected_color_skus')) return

  const current = store.getEffectiveAttribute('selected_color_skus')
  if (Array.isArray(current) && current.length > 0) return

  // The pick must obey the SAME renderability the swatch grid dims and hides
  // by, and coverage can land AFTER the catalog: deciding on a half-loaded
  // answer picks a color the grid then dims (PC380, Jake 2026-10-27). So park
  // the pick while coverage is in flight and re-run it (every guard above
  // re-checked) when coverage settles, or after a bounded wait without it.
  // isArtifiUnsupported fails open (false) while loading / on error / for an
  // empty map, so the timeout path picks among colors not KNOWN unrenderable.
  if (!coverageSettled && artifiCoverageSku.value && artifiCoverageLoading.value) {
    clearPendingDefault()
    pendingDefaultMeta = newMeta
    pendingDefaultTimer = setTimeout(() => {
      const parked = pendingDefaultMeta
      clearPendingDefault()
      if (parked) applyDefaultColorFromMeta(parked, { coverageSettled: true })
    }, DEFAULT_COLOR_COVERAGE_WAIT_MS)
    return
  }
  clearPendingDefault()

  selectDefaultColor(store, newMeta.colors, {
    // Jake, 2026-10-29: a style that renders no color gets NO default; the
    // Colors panel then asks the user to pick one.
    isRenderable: (color) => !artifiNoColorRenders.value
      && !isArtifiUnsupported(color?.name ?? color?.color_sku),
  })
}

// NOTE (regression #2003 fix-forward): publishing supplierMeta +
// supplierMetaLoading to the store was HOISTED to ProductEditor.vue so the
// producer runs on product open regardless of which left-rail tab is
// active. ContextPanel v-if-mounts this panel ONLY on the Supplier & SKU
// tab, so wiring the store from here left supplierMeta null forever on the
// Colors tab (ColorsPanel hung on "Loading colors..."). The editor shell is
// now the SOLE store-meta writer; this panel keeps its own local
// useSanmarProductMeta instance purely for its grid + the catalog auto-fill
// below. Do NOT re-add store.setSupplierMeta / setSupplierMetaLoading here
// -- a second writer re-introduces the loading-flag race across tabs.
watch(
  () => meta.meta.value,
  (newMeta) => {
    if (!newMeta) return

    // New-product convenience default, the COLOR counterpart of the default
    // size set in applySwap (Jake, 2026-09-01): a new product should land on
    // Sizes & Colors with at least one color already ENABLED, so the
    // distributor never faces an empty grid that previews nothing.
    // Priority: blackish -> whiteish -> first in the catalog.
    //
    // WHY HERE AND NOT IN ColorsPanel ON LOAD. The trigger is a resolved
    // meta payload, which only ever follows a deliberate user act (picking a
    // SKU, or the manual "Look up"). Applying this on panel MOUNT instead
    // would be indistinguishable from re-filling a list the user had just
    // emptied: `selected_color_skus` is `nullable=False` with a `'[]'`
    // server default, so a never-configured product and a deliberately
    // CLEARED one are byte-identical in the database. Keying on the user
    // action is what keeps `colors-remove-all` sticky.
    //
    // WHY NOT IN applySwap. The colors come from THIS async fetch, and at
    // applySwap time the catalog is not known yet. Guarding on the resolved
    // payload also means we never write during the loading window, where an
    // empty catalog means "not loaded" rather than "no colors". The default
    // SIZES follow the same rule (applyDefaultSizesFromMeta, below).
    //
    // A CONFIRMED destructive swap's color / image / name clean-up is NOT run
    // here: it is armed in the store and run by the editor shell, so it
    // survives this panel unmounting (useSkuSwapCleanup, card #349). What is
    // left is the empty-only auto-fill, which never overwrites a value.
    applyDefaultColorFromMeta(newMeta)
    applyDefaultSizesFromMeta(newMeta)

    // Cleaned + entity-decoded catalog title, reused for both the product
    // name and the derived short description.
    const cleanedTitle = newMeta.name
      ? decodeHtmlEntities(cleanCatalogName(newMeta.name, newMeta.sku))
      : ''

    const attrs = store.serverState?.data?.attributes ?? {}

    if (cleanedTitle) {
      if (isEmptyOrPlaceholderName(attrs.product_name)) {
        store.updateField('product_name', cleanedTitle)
      }

      // short_description has no catalog source; derive it from the cleaned
      // SKU-name title (Jake's call). Fill only when empty.
      const existingShort = attrs.short_description
      if (existingShort == null || existingShort === '') {
        store.updateField('short_description', cleanedTitle)
      }
    }

    // Only long_description has a catalog source (meta.description). Fill
    // when empty; never overwrite an existing/user-typed value.
    if (newMeta.description) {
      const existingLong = attrs.long_description
      if (existingLong == null || existingLong === '') {
        store.updateField(
          'long_description',
          decodeHtmlEntities(newMeta.description),
        )
      }
    }
  },
)

onMounted(() => {
  const preexisting
    = store.serverState?.data?.attributes?.product_item_number
  if (preexisting) {
    // Seed `selectedSku` so the autocomplete displays the current SKU
    // immediately. Without this, the field reads empty even though the
    // meta grid (populated once the fetch below resolves) shows catalog
    // values for the same SKU -- a confusing contract violation. Once meta
    // resolves, the watch() below replaces this placeholder with the
    // friendly name from the supplier response.
    //
    // This is deliberately NOT gated on the supplier: it is a local
    // display seed, costs no request, and is correct for every supplier.
    selectedSku.value = { id: preexisting, name: preexisting }
  }

  // THE MOUNT FETCH WAITS FOR THE SUPPLIER LIST. THIS ORDERING IS THE FIX.
  //
  // This used to be fire-and-forget with the comment "awaiting it here
  // would delay the meta fetch for a pre-existing SKU behind an unrelated
  // round trip". The round trip is not unrelated: `supplier` is
  // initialised to a pre-load 'sanmar' default, so a fetch issued before
  // the list lands asks SANMAR about the SKU regardless of which supplier
  // the product actually uses. For an S&S or local-vendor product that
  // 404s and paints "Unknown SanMar SKU" over a supplier the distributor
  // never picked -- the reported bug, in its most common path, since this
  // is how the panel loads an existing product.
  //
  // A list that FAILS (after the loader's retries) issues no request at
  // all -- the right outcome, because we would not know whose catalog to
  // ask. The shell usually loaded the list already; then this runs at once.
  if (supplierListLoaded.value) {
    onSupplierListLoaded()
    // COLD OPEN: the shell usually loaded the list before this panel
    // mounted, so supplierListLoaded never changes and the inventory watch
    // below has nothing to fire on. Request it here, after the selection
    // has been synced to the product's real supplier. When the list is
    // still loading, the watch fires on the false -> true flip instead.
    maybeFetchInventory()
  } else if (supplierListFailed.value) onSupplierListFailed()
  else supplierOptionsSource.load()
})

// A failed swap lookup puts the previous SKU back in the store
// (failSwapCleanup); the autocomplete must follow, or it keeps showing the
// SKU that was just rolled back. Keyed on the armed clean-up being consumed
// (success and failure both do that) and acts only when the store's SKU and
// the display disagree, so a successful swap is a no-op here.
watch(
  () => store.pendingSwapCleanup,
  (now, before) => {
    if (now || !before) return
    // The supplier travels with the SKU: a rollback puts the previous
    // supplier back in the store, so the picker follows it (card #542).
    syncSupplierSelection()
    const current = (store.getEffectiveAttribute('product_item_number') ?? '')
      .toString()
      .trim()
    if ((selectedSku.value?.id ?? '') === current) return
    selectedSku.value = current ? { id: current, name: current } : null
    if (current) fetchMetaFor(current, currentStyleId.value)
  },
)

// Update `selectedSku` once SanMar meta resolves so the autocomplete
// shows the friendly name (e.g. "Port Authority Bucket Hat C948")
// instead of the bare SKU placeholder seeded onMounted.
watch(
  () => meta.meta.value,
  (newMeta) => {
    if (newMeta && newMeta.sku && selectedSku.value?.id === newMeta.sku) {
      selectedSku.value = {
        id: newMeta.sku,
        name: newMeta.name ?? newMeta.sku,
      }
    }
  },
)

/* ============================================================
   Live Inventory card -- wired to PromoStandards (issue #5).

   Data flow:
     onMounted / SKU watcher -> inventoryStore.fetch(supplier, sku)
     Refresh button          -> inventoryStore.refresh(supplier, sku)

   The store keys by (supplier, sku) and the backend has its own 30-min
   Redis TTL cache, so opening + reopening the editor on the same SKU
   doesn't spam SanMar.

   When the SKU is empty or the supplier isn't supported, we render
   the same deterministic sample table the design bundle ships, so a
   fresh CampaignProduct still shows the IA reviewably.
   ============================================================ */

// Baseline column set used for the sample-data path (before any live
// payload arrives) and as the fallback for an empty/unavailable response.
// The live path computes its own union from the response data so that
// any size SanMar reports (5XL, 6XL, ...) gets a column. Hardcoding XS-4XL
// here used to silently drop sizes outside that range (e.g., Gildan 18000
// carries 5XL stock).
const BASELINE_INVENTORY_SIZES = Object.freeze([
  'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL',
])

// Suppliers the backend knows how to query today. Other supplier
// values (S&S Activewear, Other) drop the panel into stub mode so we
// don't fire a request the backend will 400 on.

// Read the EFFECTIVE SKU (dirty over serverState), NOT serverState alone.
// When the user picks a new SKU, applySwap writes product_item_number to
// the editor's dirty map via updateField -- but serverState is not touched
// until Save. Reading serverState here meant the Live Inventory watcher
// below (keyed on [supplier, currentSku]) never saw the unsaved SKU, so
// the inventory grid stayed on the OLD SKU until Save+reload. Reading the
// effective value makes the panel react to a pre-save SKU change, matching
// how colors/sizes/pricing already refresh (they read store.supplierMeta,
// repopulated by the meta watcher). Card https://trello.com/c/D1Hn2K0u
// (pebble 4).
const currentSku = computed(
  () => store.getEffectiveAttribute('product_item_number') ?? null,
)

// The persisted supplier style id (S&S styleID) beside `currentSku`, read
// the same effective way so a pre-save pick is followed too. null for a
// product with none; inventory then keys on the SKU alone, as before.
const currentStyleId = computed(
  () => store.getEffectiveAttribute('supplier_style_id') ?? null,
)

/** Inventory request options, with the style id only when pinned. */
function inventoryOptions() {
  const opts = {
    scope: 'all',
    campaignProductId: currentCampaignProductId.value,
  }
  if (currentStyleId.value != null) opts.styleId = currentStyleId.value
  return opts
}

// Artifi coverage for this style (Part B of card
// https://trello.com/c/CH2XJ9Jq). The live-inventory tables must HIDE colors
// Artifi cannot preview, matching the ColorsPanel picker so a distributor
// never sees a color whose editor preview 500s. Keyed on the current master
// SKU, gated the SAME way ColorsPanel gates it: feed the SKU only for a
// supplier whose styles Artifi carries (artifiPreviewEligible -- SanMar or
// S&S by resolved key; before the key resolves, SanMar-named OR unset), null
// otherwise, so a named non-integrated product never fires
// GET /api/artifi/supported_color_variants. useArtifiCoverage's watcher skips
// the fetch on a null SKU, and its coverage set then stays empty -> the hide
// filter below fails open.
const isArtifiEligible = computed(() =>
  artifiPreviewEligible({
    supplierKey: store.supplierKey,
    supplierKeyResolved: store.supplierKeyResolved,
    supplierName: store.getEffectiveAttribute?.('supplier_name'),
  }),
)
const artifiCoverageSku = computed(() =>
  isArtifiEligible.value ? currentSku.value : null,
)
const {
  supportedSet: artifiSupportedSet,
  loading: artifiCoverageLoading,
  error: artifiCoverageError,
  notFound: artifiCoverageNotFound,
  styleUnsupported: artifiStyleUnsupported,
  isUnsupported: isArtifiUnsupported,
} = useArtifiCoverage(artifiCoverageSku)

// The backend STATED that Artifi can render no color of this style (PC380):
// every color is unrenderable, though the empty variant map gives
// isArtifiUnsupported nothing to reject. An SKU Artifi merely does not
// recognize (notFound) is not that verdict: it stays fail-open.
const artifiNoColorRenders = computed(
  () => artifiStyleUnsupported.value && !artifiCoverageNotFound.value,
)

// Coverage settled while a default pick was parked: run it now.
watch(artifiCoverageLoading, (loading) => {
  if (loading || !pendingDefaultMeta) return
  const parked = pendingDefaultMeta
  clearPendingDefault()
  applyDefaultColorFromMeta(parked, { coverageSettled: true })
})

// SuperAdmin toggle (hide_unpreviewable_color_variants): GATES the coverage
// hiding below. ON (the launch default) -> hide unpreviewable rows; OFF ->
// SHOW them dimmed/flagged (data-artifi-unsupported + is-artifi-unsupported)
// instead of removed. Shared read (one GET per page load, deduped) so this
// panel and ColorsPanel always agree. Fails SAFE to true (hide) while
// loading / on error.
const { hideUnpreviewable } = useHideUnpreviewableColors()

// Does the SKU on the product belong to the supplier the PICKER shows?
//
// Moving the picker changes nothing on the product until a SKU is picked
// under the new supplier (`applySwap` stages both together). In that window
// `currentSku` is still the OLD supplier's SKU, and asking the new
// supplier's inventory service about it is a request for a style it never
// sold -- a console 503 from S&S on 8086, the mirror image of the SanMar
// 404s this panel used to send for S&S styles. A product with no known
// supplier at all (the PC54 shape) is taken as matching, the same
// fail-open-on-absence the shell's catalog key applies. So is an EMPTY
// option list (a list that offered nothing); a FAILED list clears the
// selection instead (onSupplierListFailed), so nothing is queried at all.
const skuBelongsToPickedSupplier = computed(() => {
  const id = store.getEffectiveAttribute?.('supplier_id')
  const name = store.getEffectiveAttribute?.('supplier_name')
  if (id == null && !(name ?? '').toString().trim()) return true
  if (supplierItems.value.length === 0) return true
  return supplierKeyForProduct(id, name, supplierItems.value) === supplier.value
})

const canQueryLiveInventory = computed(
  () => hasCatalogIntegration(supplier.value)
    && !!currentSku.value
    && skuBelongsToPickedSupplier.value,
)

/**
 * This supplier has no live-inventory service behind it at all.
 *
 * Distinct from `!canQueryLiveInventory`, which is ALSO true for an
 * integrated supplier that simply has no SKU picked yet. That difference
 * is the whole point: "pick a SKU and we will query" and "there is nothing
 * to query, ever" are different sentences, and collapsing them is how the
 * card ended up telling an S&S user to select a supported supplier while
 * S&S was selected.
 */
const supplierHasNoLiveInventory = computed(
  () => !hasCatalogIntegration(supplier.value),
)

// Per-key live record from the store. Falls back to an empty record so
// the template can render unconditionally on first mount before any
// fetch has resolved.
const liveInventory = computed(() => {
  if (!canQueryLiveInventory.value) {
    return { loading: false, error: null, data: null }
  }
  return currentStyleId.value == null
    ? inventoryStore.get(supplier.value, currentSku.value)
    : inventoryStore.get(supplier.value, currentSku.value, currentStyleId.value)
})

// Derived size-column set: the union of `sizes` keys across all colors
// in the live payload, ordered by the canonical SanMar progression (see
// utils/inventorySizes.js). Falls back to the baseline list when no live
// data is present so the sample table still renders with the expected
// columns pre-fetch.
const INVENTORY_SIZES = computed(() => {
  if (hasLiveInventoryColors.value) {
    const derived = deriveInventorySizes(liveInventory.value.data.colors)
    if (derived.length > 0) return derived
  }
  return BASELINE_INVENTORY_SIZES
})

// The badge names the SELECTED supplier, resolved from the server's own
// option list. It was the literal 'SanMar', so the Live inventory card
// announced PROMOSTANDARDS - SANMAR over S&S data -- a provenance claim
// about numbers that did not come from SanMar. A badge that can only say
// one thing is not a badge, it is a label pretending to be evidence.
const supplierBadgeLabel = computed(() =>
  supplierDisplayName(supplier.value, supplierItems.value),
)

// ---------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for "is the table showing real supplier data?"
// (issue #2769).
//
// This predicate is THE ONE condition. The row builder (inventoryRows),
// the disclosure notice, the per-row sample marker, and the size-column
// derivation all read it, so the disclosure cannot drift away from the
// fabrication it is disclosing.
//
// Before this, the notice was gated on `!liveInventory.data` while the
// row builder ALSO fell through to sample rows when `data.colors` was
// empty and never consulted `liveInventory.error`. The two agreed only
// when `data` was null. When the supplier returned a payload with an
// empty colors array -- a product genuinely out of stock in every colour,
// or a lookup that matched nothing -- `data` was truthy, the notice was
// SUPPRESSED, and four invented SanMar SKUs rendered as real inventory. A
// distributor could not tell fabricated stock from real stock.
//
// "Out of stock in every colour" is a REAL business state. It must render
// as an honest empty result, never as fabricated numbers.
// ---------------------------------------------------------------------
const hasLiveInventoryColors = computed(() => {
  const live = liveInventory.value.data
  return !!live && Array.isArray(live.colors) && live.colors.length > 0
})

// True whenever ANY row in the table is fabricated. Exactly the negation
// of hasLiveInventoryColors, named positively because that is how the
// template reads it. Every fabricating state -- no SKU, unsupported
// supplier, loading, error, no data, data-with-empty-colors -- lands here.
const liveInventoryIsStub = computed(() => !hasLiveInventoryColors.value)

// The supplier answered, successfully, that it stocks nothing for this
// SKU. Distinct from "we have no data": this is a real, reportable
// business state and gets an honest empty table rather than sample rows.
const liveInventoryIsEmptyResult = computed(() => {
  const live = liveInventory.value.data
  return (
    !!live
    && Array.isArray(live.colors)
    && live.colors.length === 0
    && !liveInventory.value.loading
    && !liveInventory.value.error
  )
})

// Sample rows exist so the design IA stays reviewable on a freshly
// created CampaignProduct (#1508). They are kept for that purpose ONLY --
// suppressed once the supplier has actually answered, because a real
// "nothing in stock" answer must not be overwritten with invented stock.
const showsSampleInventory = computed(
  () =>
    liveInventoryIsStub.value
    && !liveInventoryIsEmptyResult.value
    // Never fabricate stock for a supplier that HAS no inventory service.
    // Sample rows exist to keep the design IA reviewable on a fresh
    // CampaignProduct (#1508) -- a purpose that assumes real data is
    // coming. Here none ever is, so the rows are not a placeholder for a
    // pending answer; they are SanMar colors (Athletic Heather, Jet Black)
    // attributed to a supplier that never reported them.
    && !supplierHasNoLiveInventory.value,
)

/**
 * Copy for a supplier with no live-inventory service at all.
 *
 * Names the supplier, and says nothing about SanMar or about "supported
 * suppliers". The old stub copy told the user to select a supported
 * supplier -- advice that reads as an accusation when they have already
 * selected the supplier they intend to use, and which leaks that a
 * capability tier exists.
 */
const noLiveInventoryText = computed(() => {
  // A FAILED vendor list leaves no selection (onSupplierListFailed). That
  // is "we cannot tell yet", not "this supplier publishes no stock".
  if (supplierListFailed.value) {
    return 'Live inventory will show once your suppliers load.'
  }
  const label = supplierDisplayName(supplier.value, supplierItems.value)
  return (
    `Live inventory is not available for ${label}. `
    + 'Stock levels for this supplier are not published to ThreadFlow, so '
    + 'none are shown here.'
  )
})

const stubNoticeText = computed(() => {
  if (liveInventoryIsEmptyResult.value) {
    return (
      'The supplier reported no stock for this SKU in any colour. '
      + 'This is a live result, not sample data.'
    )
  }
  if (!canQueryLiveInventory.value) {
    // Reaching here means the SELECTED supplier has an inventory service
    // (`supplierHasNoLiveInventory` is handled above), so the missing piece
    // is a SKU of THAT supplier. Name it: the old copy said "a supported
    // supplier (SanMar)" over an S&S selection (GH #4424).
    const label = supplierDisplayName(supplier.value, supplierItems.value)
    return (
      `Live inventory becomes available once a ${label} SKU is selected. `
      + 'Every figure below is SAMPLE DATA shown so the layout is '
      + 'reviewable -- not real supplier stock.'
    )
  }
  if (liveInventory.value.loading) {
    return (
      'Querying the supplier\'s PromoStandards Inventory service... '
      + 'Every figure below is SAMPLE DATA until it returns.'
    )
  }
  if (liveInventory.value.error) {
    return (
      'Live inventory could not be loaded, so every figure below is '
      + 'SAMPLE DATA -- not real supplier stock.'
    )
  }
  return (
    'No inventory data has loaded yet for this SKU. Every figure below '
    + 'is SAMPLE DATA -- not real supplier stock.'
  )
})

// Human-friendly "Queried N min ago" using the backend's queried_at.
const lastQueriedLabel = computed(() => {
  const at = liveInventory.value.data?.queried_at
  if (!at) return 'Not yet queried'
  const ts = new Date(at).getTime()
  if (Number.isNaN(ts)) return 'Queried recently'
  const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000))
  if (seconds < 45) return 'Queried just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `Queried ${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Queried ${hours} hr ago`
  const days = Math.round(hours / 24)
  return `Queried ${days} d ago`
})

// Fallback swatch color when neither vendor_color_hex.hex_value NOR
// swatch_url is available for a row. Matches the legacy placeholder
// hex used before the backend join landed -- visible only when the
// data source has a genuine gap.
const GREY_PLACEHOLDER_HEX = '#cfd6dd'

// Sample colors used when no `selected_color_skus` are present on the
// CampaignProduct AND no live data has arrived. These keep the design IA
// visible on a freshly-created product.
const SAMPLE_COLORS = [
  { sku: 'PC78H-ATHHTH', name: 'Athletic Heather', hex: '#a8b1be' },
  { sku: 'PC78H-NAVY', name: 'Navy', hex: '#1f2c4a' },
  { sku: 'PC78H-BLACK', name: 'Jet Black', hex: '#1a1a1a' },
  { sku: 'PC78H-RED', name: 'True Red', hex: '#c8102e' },
]



function buildSampleRow(color) {
  const bySize = {}
  // Sample rows seed every column in the current INVENTORY_SIZES set so
  // the table renders fully populated. With no live data this resolves to
  // BASELINE_INVENTORY_SIZES; in the rare case where a sample row renders
  // alongside live data, it follows the derived column set.
  //
  // QUANTITIES ARE NULL ON PURPOSE (#2769). These rows exist so the design
  // IA is reviewable on a freshly-created product -- that need is met by the
  // SHAPE of the table, not by numbers in it. Each cell previously carried
  // ``seededStock()``, a hash of sku+size that rendered a specific,
  // plausible count. A distributor cannot tell an invented 47 from a real
  // one, and the colour names here are genuine SanMar names, which made the
  // numbers read as genuine too.
  //
  // ``formatQty`` already renders null as an em-dash and ``classifyQty``
  // already maps it to 'na', so emitting null reuses the honest path that
  // was always there rather than adding a second one.
  INVENTORY_SIZES.value.forEach((sz) => {
    bySize[sz] = null
  })
  // Status is DERIVED from quantities; with no quantities there is nothing
  // to state. ``statusLabel`` passes an unknown value through, so this
  // renders blank rather than asserting "In stock" about nothing.
  const status = null
  // is_sample drives BOTH the data attribute and the visible per-row
  // badge (#2769). Set here, at the only place fabricated rows are built,
  // so a new fabricating path cannot forget it.
  return { ...color, bySize, total: null, status, is_sample: true }
}


// Live -> view-model mapping. The backend returns:
//   colors: [{ name, sizes: { XS: N, ... }, total, status, hex, swatch_url }]
// where ``status`` is 'in_stock' | 'low' | 'out'. The table template
// uses 'in' | 'low' | 'out', so map 'in_stock' -> 'in' here.
//
// ``hex`` and ``swatch_url`` are joined server-side from the
// ``vendor_color_hex`` table; either or both may be null when the
// table has no row for the color name. The swatch render order is:
//   1. swatch_url present -> render swatch image with hex as the
//      background-color underneath (covers PR #1513 CDN-unreliability
//      pattern: if the image fails, the real hex still shows through)
//   2. hex only -> solid color
//   3. neither -> grey placeholder
function buildLiveRow(c) {
  // Pass the backend's sizes object through verbatim -- it already
  // carries only the sizes this color stocks. The template iterates the
  // computed INVENTORY_SIZES union across all colors, so a color missing
  // a size resolves to `undefined` in row.bySize[sz] and formatQty()
  // renders it as the no-data sentinel. Previously this function built
  // bySize against a hardcoded XS-4XL list, silently dropping 5XL+.
  const bySize = c.sizes ?? {}
  const status = c.status === 'in_stock' ? 'in' : c.status
  return {
    sku: c.name,
    name: c.name,
    hex: c.hex || GREY_PLACEHOLDER_HEX,
    swatch_url: c.swatch_url || null,
    bySize,
    total: c.total,
    status,
    // Real supplier data (#2769). Explicit rather than absent so a row
    // whose provenance was never set reads as a defect, not as live.
    is_sample: false,
  }
}

// Row source. Reads hasLiveInventoryColors -- the SAME predicate the
// disclosure notice reads (#2769) -- so the two can never disagree about
// whether the numbers on screen are real.
//
// Every row carries `is_sample`, which the template binds to
// data-inventory-sample and a visible per-row badge. Row-level marking is
// deliberate: a notice above the table is easy to scroll past, while the
// marker travels with the number the distributor is actually reading.
const inventoryRows = computed(() => {
  if (hasLiveInventoryColors.value) {
    return liveInventory.value.data.colors.map(buildLiveRow)
  }
  // Supplier answered "nothing in stock anywhere" -- an honest empty
  // table, never fabricated rows on top of a real negative result.
  if (!showsSampleInventory.value) return []
  const savedSkus = store.serverState?.data?.attributes?.selected_color_skus
  if (Array.isArray(savedSkus) && savedSkus.length > 0) {
    // Real colour names, INVENTED quantities -- the most misleading of the
    // fabricating paths, since genuine names make the numbers read as
    // genuine. buildSampleRow marks them.
    return savedSkus.map((sku) =>
      buildSampleRow({ sku, name: sku, hex: GREY_PLACEHOLDER_HEX }),
    )
  }
  return SAMPLE_COLORS.map(buildSampleRow)
})

// Set of selected color SKUs for fast lookup. Both row.name and the
// entries in selected_color_skus are English color names post-PR #1577.
const selectedColorSkuSet = computed(() => {
  const skus = store.serverState?.data?.attributes?.selected_color_skus ?? []
  return new Set(skus)
})

// Artifi coverage filter (Part B of card https://trello.com/c/CH2XJ9Jq): drop
// inventory rows whose color Artifi cannot preview, matching ColorsPanel's
// coverageFilteredColors. STRICT fail-open (Jake's call, mirrored from
// ColorsPanel): hide ONLY when the coverage fetch succeeded (not loading, no
// error) AND the supported set is non-empty. Loading / error / empty-set ->
// show every row so a transient/uncertain coverage state never blanks the
// live inventory. Stranded-color guard: NEVER hide a color already in
// selected_color_skus even if now unsupported, so the distributor can still
// see + deselect it (hiding it would strand a persisted, storefront-rendered
// color out of the editor). The In-use / Not-in-use split reads from THIS so
// both buckets stay consistent.
const artifiFilteredInventoryRows = computed(() => {
  const all = inventoryRows.value
  // SA toggle OFF -> never HIDE; unpreviewable rows stay and are dimmed/flagged
  // via isRowUnsupported() in the template. STRICT fail-open still governs the
  // DIM treatment (isArtifiUnsupported is false while loading/errored/empty).
  if (!hideUnpreviewable.value) return all
  // TWO SOURCES, ON PURPOSE (#2060) -- same shape as ColorsPanel's
  // coverageFilteredColors gate, which carries the full explanation.
  // In short: coverageReady reads supportedSet (the RAW 'supported' list,
  // "did coverage load at all") while isArtifiUnsupported reads the resolved
  // 'supported_with_variant_sku' map ("can Artifi preview THIS color"). Per
  // #2057 the per-color decision must key off the map, because residual
  // Class B colors live in the raw list but not in the map. A Class B style
  // whose map failed to rebuild ({}) therefore passes this gate and still
  // hides nothing -- two fail-opens, one correct outcome.
  const coverageReady =
    !artifiCoverageLoading.value
    && !artifiCoverageError.value
    && artifiSupportedSet.value
    && artifiSupportedSet.value.size > 0
  if (!coverageReady) return all
  const selected = selectedColorSkuSet.value
  return all.filter(
    (r) => selected.has(r.name) || !isArtifiUnsupported(r.name),
  )
})

// Dim/flag predicate for a row (SA toggle OFF path). isArtifiUnsupported
// already encodes STRICT fail-open (false while loading/errored/empty), so a
// row is dimmed only when coverage proves the color unpreviewable. Kept
// toggle-agnostic: when the toggle is ON the unsupported row is HIDDEN (never
// reaches the table), so this only visibly fires on the OFF path.
function isRowUnsupported(row) {
  return isArtifiUnsupported(row.name)
}

// Rows split into "in use" / "not in use" buckets, each alphabetically
// sorted by name (case-insensitive). Both read the Artifi-filtered rows so an
// unsupported color is absent from both tables (unless it is stranded-selected).
const inUseRows = computed(() =>
  artifiFilteredInventoryRows.value
    .filter((r) => selectedColorSkuSet.value.has(r.name))
    .slice()
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    ),
)

const notInUseRows = computed(() =>
  artifiFilteredInventoryRows.value
    .filter((r) => !selectedColorSkuSet.value.has(r.name))
    .slice()
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    ),
)

// Swatch render order (per PR #1513 pattern, extended here):
//   1. swatch_url + hex -> render image with hex as background-color
//      underneath. If SanMar's CDN regresses (the bug PR #1513 caught
//      for storefront swatches), the real hex shows through; we never
//      collapse to grey for a row that has BOTH.
//   2. hex only -> solid color
//   3. neither (sample / unseeded color) -> grey placeholder
function swatchStyle(row) {
  if (row.swatch_url) {
    return {
      backgroundColor: row.hex || GREY_PLACEHOLDER_HEX,
      backgroundImage: `url(${row.swatch_url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }
  return { backgroundColor: row.hex || GREY_PLACEHOLDER_HEX }
}

function classifyQty(q) {
  if (q == null) return 'na'
  if (q === 0) return 'out'
  if (q < 24) return 'low'
  return 'ok'
}

// No-data sentinel for size cells. The em-dash represents "this color
// doesn't stock that size", semantically distinct from 0 ("stocked, none
// in inventory"). Matches the meta-grid empty-state convention elsewhere
// in this panel and the design bundle.
const EM_DASH = '—'

function formatQty(q) {
  if (q == null) return EM_DASH
  return String(q)
}

function statusLabel(status) {
  if (status === 'in') return 'In stock'
  if (status === 'low') return 'Low'
  if (status === 'out') return 'Out'
  return status
}

// Live Inventory always fetches the full ("all") SanMar color set so the
// client can split it into the "In use" / "Not in use" sections below
// based on selected_color_skus. The legacy "Show all SanMar colors"
// toggle and the corresponding scope=selected fetch path have been
// removed -- both sections are always visible.
const currentCampaignProductId = computed(
  () => store.serverState?.data?.id ?? null,
)

async function refreshInventory() {
  if (!canQueryLiveInventory.value) return
  if (awaitingStyleAnswer.value) return
  await inventoryStore.refresh(
    supplier.value, currentSku.value, inventoryOptions(),
  )
}

// Auto-fetch once the supplier list settles, and on supplier/SKU change.
// The store de-dupes concurrent in-flight requests and the backend caches;
// this is safe to invoke on every watch tick.
//
// WAITS FOR THE SUPPLIER LIST. `supplier` starts on a pre-load SanMar
// default, and this used to fire from onMounted against it -- before
// `syncSupplierSelection` moved the selection to the product's real
// supplier. Every non-SanMar product therefore sent its SKU to SanMar's
// inventory service on each editor open: a real outbound PromoStandards
// call, and a console 503 for a SKU SanMar never sold. Same wait the meta
// fetch in onMounted already does.
// An UNPINNED name may be shared by several styles (S&S "5200" is C2 Sport,
// Gildan and Bayside), and the product lookup is what says so (409
// ambiguous_style). Asking S&S for inventory by the bare name first sends a
// call that cannot be answered (503 "Product Id not found") and paints an
// error banner beside the picker. So hold the inventory call while that
// lookup is in flight, and skip it while the picker is showing (Bug Intake
// #451): ask which style first, fetch after pinning. A pinned style never
// waits -- its id is the answer.
const awaitingStyleAnswer = computed(
  () => currentStyleId.value == null
    && (!!meta.loading.value || !!meta.error.value?.ambiguousStyle),
)

async function maybeFetchInventory() {
  if (!supplierListLoaded.value) return
  if (!canQueryLiveInventory.value) return
  if (awaitingStyleAnswer.value) return
  await inventoryStore.fetch(
    supplier.value, currentSku.value, inventoryOptions(),
  )
}

watch(
  () => [
    supplierListLoaded.value,
    supplier.value,
    currentSku.value,
    currentStyleId.value,
    awaitingStyleAnswer.value,
  ],
  () => {
    maybeFetchInventory()
  },
)
</script>

<style scoped>
.supplier-sku-header {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
}

.supplier-sku-header-icon {
  flex: 0 0 auto;
  font-size: 28px;
}

.supplier-sku-header-text {
  flex: 1 1 auto;
  min-width: 0;
}

.supplier-sku-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 1.2;
}

.supplier-sku-subtitle {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}

.supplier-sku-eyebrow {
  flex: 0 0 auto;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  align-self: flex-start;
}

.supplier-sku-field-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.supplier-sku-supplier-select {
  flex: 0 0 200px;
  min-width: 0;
}

/*
  Entitlement exclusion note. Takes the full row so it sits under the
  supplier/SKU controls rather than competing with them for horizontal
  space -- it is an explanation, not a control.
*/
.supplier-sku-supplier-empty {
  flex: 1 0 100%;
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface-variant, 97, 97, 97));
}

/* Same treatment as the live-inventory error banner below, so the two
   failure states in this panel read as one family. */
.supplier-sku-supplier-load-error {
  margin: 0 0 16px;
  padding: 8px 12px;
  background: #fdecec;
  border-left: 3px solid #d64545;
  border-radius: 2px;
  font-size: 12px;
  color: #7a1f1f;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 12px;
}

.supplier-sku-supplier-load-retry {
  margin-left: auto;
  background: transparent;
  border: 1px solid #d64545;
  color: #7a1f1f;
  border-radius: 3px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.supplier-sku-supplier-load-retry:hover {
  background: #d64545;
  color: #fff;
}

.supplier-sku-typeahead {
  flex: 1 1 auto;
  min-width: 0;
}

.supplier-sku-lookup-btn {
  flex: 0 0 auto;
}

.supplier-sku-meta-wrap {
  position: relative;
}

.supplier-sku-meta-loading {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.supplier-sku-meta-alert {
  margin-bottom: 12px;
}

.supplier-sku-meta-fallback-notice {
  margin: 4px 0 12px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-left: 3px solid #94a3b8;
  border-radius: 2px;
  font-size: 12px;
  color: #475569;
  line-height: 1.4;
}

/* Card #243: informational, non-alarming notice for a style Artifi cannot
   preview. Amber accent (attention, not error) keeps it distinct from the
   red meta-error alert above. */
.supplier-sku-artifi-unsupported {
  margin: 4px 0 12px;
  padding: 8px 12px;
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
  border-radius: 2px;
  font-size: 12px;
  color: #92400e;
  line-height: 1.4;
}

.supplier-sku-meta-grid {
  display: grid;
  /* Fixed 4-column layout so the compact identity fields sit on ONE row:
     SKU | Product name | Brand | Base cost. Row 2 carries Available sizes
     and Fabric & weight side-by-side (each --half spans 2 of 4). Product
     description spans the full row (--wide). min-width:0 on every cell +
     word-break on the value keep long content from forcing horizontal
     scroll when the preview wraps the grid to a narrow column. */
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 20px;
}

.supplier-sku-meta-grid.is-fallback .supplier-sku-meta-value {
  color: #475569;
  font-style: italic;
}

.supplier-sku-meta-grid.is-loading {
  opacity: 0.6;
}

.supplier-sku-meta-cell {
  min-width: 0;
}

.supplier-sku-meta-cell--half {
  grid-column: span 2;
}

.supplier-sku-meta-cell--wide {
  grid-column: 1 / -1;
}

.supplier-sku-meta-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 2px;
}

.supplier-sku-meta-value {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  word-break: break-word;
}

/* =====================================================================
   Preview-image split (no-card UI polish).

   Two-column flex row INSIDE the top card body. The left column (fields +
   meta) flexes to fill the majority of the width; the right column is a
   fixed comfortable width for the preview. flex-wrap + a min-width on the
   left column make the preview wrap ABOVE the fields on narrow widths so
   the body never scrolls horizontally. Matches the plain-CSS flex idiom
   already used by .supplier-sku-field-row (no grid framework introduced).
   ===================================================================== */
.supplier-sku-body {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
}

.supplier-sku-body-left {
  flex: 1 1 340px;
  min-width: 0;
}

.supplier-sku-preview {
  flex: 0 0 240px;
  max-width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.supplier-sku-preview-img {
  display: block;
  width: 100%;
  max-width: 240px;
  aspect-ratio: 4 / 5;
  object-fit: contain;
  /* White so the letterbox bars around a `contain`-fit product shot are
     white, not grey (SanMar shots are on a white studio background). */
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
}

.supplier-sku-preview-placeholder {
  width: 100%;
  max-width: 240px;
  aspect-ratio: 4 / 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #f1f3f5;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
  padding: 12px;
}

.supplier-sku-preview-placeholder-icon {
  font-size: 40px;
  color: rgba(0, 0, 0, 0.3);
}

.supplier-sku-preview-placeholder-text {
  font-size: 12px;
  line-height: 1.3;
}

/* =====================================================================
   Day 8 Item B3 - Live Inventory card styles.
   Spacing/colors track design tokens from
   planning/design-handoffs/handoff_2026-06-13/2026-06-13-product-editor/design/editor-a.css
   ===================================================================== */

.live-inventory-card {
  margin-top: 16px;
}

.live-inventory-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.live-inventory-header-icon {
  font-size: 22px;
  color: rgba(0, 0, 0, 0.6);
}

.live-inventory-title {
  font-weight: 600;
  font-size: 16px;
}

.live-inventory-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  font-weight: 600;
  color: #0a6cb0;
  background: #e3f2fd;
  border: 1px solid #cfe6fb;
  border-radius: 50px;
  padding: 2px 9px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.live-inventory-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00b87a;
}

.live-inventory-sync {
  font-size: 11px;
  color: var(--tf-fg-4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.live-inventory-refresh {
  margin-left: auto;
}

.live-inventory-section {
  margin-bottom: 16px;
}

.live-inventory-section:last-of-type {
  margin-bottom: 0;
}

.live-inventory-section-heading {
  font-size: 12px;
  font-weight: 600;
  color: #5a6a82;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin: 0 0 6px;
}

.live-inventory-empty {
  margin: 0 0 8px;
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px dashed #e0e4e8;
  border-radius: 3px;
  font-size: 12px;
  color: var(--tf-fg-4);
  font-style: italic;
}

.live-inventory-body {
  padding-top: 0;
}

.live-inventory-stub-notice {
  margin: 0 0 12px;
  padding: 8px 12px;
  background: #fff7e6;
  border-left: 3px solid #efc879;
  border-radius: 2px;
  font-size: 12px;
  color: #6a5008;
  line-height: 1.4;
}

.live-inventory-stale-banner {
  margin: 0 0 12px;
  padding: 8px 12px;
  background: #fff9e6;
  border-left: 3px solid #f0c14b;
  border-radius: 2px;
  font-size: 12px;
  color: #745510;
  line-height: 1.4;
}

.live-inventory-error-banner {
  margin: 0 0 12px;
  padding: 8px 12px;
  background: #fdecec;
  border-left: 3px solid #d64545;
  border-radius: 2px;
  font-size: 12px;
  color: #7a1f1f;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 12px;
}

.live-inventory-error-retry {
  margin-left: auto;
  background: transparent;
  border: 1px solid #d64545;
  color: #7a1f1f;
  border-radius: 3px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.live-inventory-error-retry:hover {
  background: #d64545;
  color: #fff;
}

.live-inventory-table-wrap {
  overflow-x: auto;
}

.live-inventory-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: Roboto, sans-serif;
}

.live-inventory-table th,
.live-inventory-table td {
  padding: 7px 9px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  border-bottom: 1px solid #f4f6f8;
}

.live-inventory-table thead th {
  font-size: 10px;
  font-weight: 600;
  color: var(--tf-fg-4);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  background: #fafbfc;
}

.live-inventory-table th.live-inventory-col-color,
.live-inventory-table td.live-inventory-col-color {
  text-align: left;
  min-width: 150px;
}

.live-inventory-color-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.live-inventory-swatch {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}

.live-inventory-color-name {
  font-size: 12px;
  font-weight: 500;
  color: #212121;
}

/*
 * SA toggle OFF: an Artifi-unpreviewable inventory row is shown DIMMED +
 * flagged (slashed-eye) rather than hidden. Dim the whole row's content so
 * it reads "available, no preview" without disabling it.
 */
.is-artifi-unsupported .live-inventory-swatch,
.is-artifi-unsupported .live-inventory-color-name,
.is-artifi-unsupported .live-inventory-q,
.is-artifi-unsupported /* Sample-data marking (#2769). A fabricated row is dimmed and carries a
   visible "Sample" badge beside its status, so a distributor reading a
   quantity can tell at the ROW level that it is not real supplier stock --
   a notice above the table is too easy to scroll past. */
.live-inventory-sample-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border: 1px solid var(--tf-warn-fg, #8a6d1f);
  border-radius: 9px;
  background: var(--tf-warn-bg, #fdf3d7);
  color: var(--tf-warn-fg, #8a6d1f);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
}

tr.is-inventory-sample {
  opacity: 0.72;
  font-style: italic;
}

.live-inventory-status {
  opacity: 0.5;
}

.live-inventory-unsupported-eye {
  font-size: 15px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.55);
  flex-shrink: 0;
}

.live-inventory-q {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 22px;
  padding: 0 7px;
  border-radius: 50px;
  font-variant-numeric: tabular-nums;
  font-size: 11.5px;
  font-weight: 600;
}

.live-inventory-q-ok {
  color: #5a6a82;
  font-weight: 500;
}

.live-inventory-q-low {
  color: #9a6300;
  background: #ffe7ad;
  box-shadow: inset 0 0 0 1px #efc879;
}

.live-inventory-q-out {
  color: #fff;
  background: #d64545;
  box-shadow: 0 1px 2px rgba(214, 69, 69, 0.4);
}

.live-inventory-q-na {
  color: #cfd6dd;
  font-weight: 400;
}

.live-inventory-q-total {
  color: #133752;
  font-weight: 700;
}

.live-inventory-cell-low {
  background: #fffaf0;
}

.live-inventory-cell-out {
  background: #fdf2f2;
}

.live-inventory-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 8px;
  border-radius: 50px;
}

.live-inventory-status-in {
  background: #e8f5e9;
  color: #2e7d32;
}

.live-inventory-status-low {
  background: #fff7e6;
  color: #b26a00;
}

.live-inventory-status-out {
  background: #fdecec;
  color: #c62828;
}

.live-inventory-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 12px;
  margin-top: 8px;
  border-top: 1px solid #f0f0f0;
  background: #fafbfc;
  font-size: 11px;
  color: var(--tf-fg-4);
}

.live-inventory-legend-note {
  margin-left: auto;
  text-align: right;
}
</style>
